import React, { useState, useEffect, useRef } from 'react';

const BreachBlockIQ = () => {
  const [currentStep, setCurrentStep] = useState('intro');
  const [currentCategory, setCurrentCategory] = useState(0);
  const [answers, setAnswers] = useState({});
  const [businessInfo, setBusinessInfo] = useState({ name: '', industry: '', size: '', email: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('onetime');
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(29.99);
  const [activeTab, setActiveTab] = useState('business');
  const [reportSaved, setReportSaved] = useState(false);
  const [showSaveReminder, setShowSaveReminder] = useState(false);
  const paypalRef = useRef(null);
  const [hasStoredReport, setHasStoredReport] = useState(false);
  const [printPrompted, setPrintPrompted] = useState(false);

  // Check for stored report on initial load
  useEffect(() => {
    const storedReport = localStorage.getItem('breachblockiq_report');
    const storedBusiness = localStorage.getItem('breachblockiq_business');
    if (storedReport && storedBusiness) {
      try {
        const parsedReport = JSON.parse(storedReport);
        const parsedBusiness = JSON.parse(storedBusiness);
        setReport(parsedReport);
        setBusinessInfo(parsedBusiness);
        setPaymentComplete(true);
        setHasStoredReport(true);
        console.log('Found stored report for:', parsedBusiness.name);
      } catch (e) {
        console.log('Error parsing stored report');
        localStorage.removeItem('breachblockiq_report');
        localStorage.removeItem('breachblockiq_business');
      }
    }
  }, []);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Auto-prompt print dialog when report loads (first time only)
  useEffect(() => {
    if (currentStep === 'report' && report && !printPrompted) {
      setPrintPrompted(true);
      // Small delay to let report render fully
      setTimeout(() => {
        const shouldPrint = window.confirm(
          '📄 Save your report as PDF?\n\nClick OK to open the print dialog (choose "Save as PDF" as your printer).\n\nClick Cancel to view the report first.'
        );
        if (shouldPrint) {
          // Trigger the downloadPdf function by clicking the button
          const downloadBtn = document.querySelector('[data-download-pdf]');
          if (downloadBtn) downloadBtn.click();
        }
      }, 500);
    }
  }, [currentStep, report, printPrompted]);

  // Save report to localStorage when generated
  useEffect(() => {
    if (report && businessInfo.email && currentStep === 'report') {
      localStorage.setItem('breachblockiq_report', JSON.stringify(report));
      localStorage.setItem('breachblockiq_business', JSON.stringify(businessInfo));
      console.log('Report saved to localStorage');
    }
  }, [report, businessInfo, currentStep]);

  // Warn before leaving if report not saved
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentStep === 'report' && !reportSaved) {
        e.preventDefault();
        e.returnValue = 'Your report may not be saved. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentStep, reportSaved]);

  // Promo codes validated server-side (see /api/validate-promo.js)

  // TechShield KC Brand Colors
  const colors = {
    orange: '#E8792B',
    orangeLight: 'rgba(232, 121, 43, 0.15)',
    orangeBorder: 'rgba(232, 121, 43, 0.4)',
    black: '#1a1a1a',
    darkBg: '#0d0d0d',
    darkCard: '#161616',
    darkCardHover: '#1f1f1f',
    white: '#ffffff',
    gray: '#a3a3a3',
    grayLight: '#d4d4d4',
    grayDark: '#525252'
  };

  // Sanitize user input to prevent XSS
  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // Simple markdown to HTML converter
  const markdownToHtml = (markdown) => {
    if (!markdown) return '';
    
    let html = markdown
      // Escape HTML first
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Headers
      .replace(/^### (.*$)/gm, '<h3 style="color: #ffffff; font-size: 1.1rem; margin: 1.5rem 0 0.75rem; font-weight: 600;">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 style="color: #E8792B; font-size: 1.3rem; margin: 2rem 0 1rem; font-weight: 700; border-bottom: 1px solid #333; padding-bottom: 0.5rem;">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 style="color: #ffffff; font-size: 2rem; margin: 1rem 0; font-weight: 700;">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #ffffff;">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Horizontal rule
      .replace(/^---$/gm, '<hr style="border: none; border-top: 1px solid #333; margin: 1.5rem 0;">')
      // Bullet points
      .replace(/^- (.*$)/gm, '<li style="margin: 0.5rem 0; margin-left: 1.5rem;">$1</li>')
      // Tables - basic support
      .replace(/\|:?-+:?\|/g, '') // Remove separator rows
      .replace(/^\|(.*)\|$/gm, (match, content) => {
        const cells = content.split('|').map(cell => cell.trim());
        return '<tr>' + cells.map(cell => `<td style="padding: 0.5rem; border: 1px solid #333;">${cell}</td>`).join('') + '</tr>';
      })
      // Line breaks
      .replace(/\n\n/g, '</p><p style="margin: 1rem 0; line-height: 1.7;">')
      .replace(/\n/g, '<br>');
    
    // Wrap in paragraph
    html = '<p style="margin: 1rem 0; line-height: 1.7;">' + html + '</p>';
    
    // Clean up empty paragraphs
    html = html.replace(/<p[^>]*><\/p>/g, '');
    html = html.replace(/<p[^>]*><br><\/p>/g, '');
    
    return html;
  };

  // Scroll to top whenever step or category changes
  useEffect(() => {
    // Multiple methods for cross-browser/mobile compatibility
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    // Also try scrolling the main element if exists
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  }, [currentStep, currentCategory, showPaywall]);

  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalSdkType, setPaypalSdkType] = useState(null); // 'onetime' or 'subscription'

  // Determine SDK type needed
  const neededSdkType = selectedPlan === 'onetime' ? 'onetime' : 'subscription';

  // Load PayPal SDK and render button when paywall opens
  useEffect(() => {
    if (showPaywall && !paymentComplete && businessInfo.email && businessInfo.email.includes('@')) {
      
      // Only reload SDK if type changes (onetime vs subscription)
      if (paypalSdkType !== neededSdkType) {
        setPaypalLoading(true);
        
        // Remove any existing PayPal scripts
        const existingScripts = document.querySelectorAll('script[src*="paypal.com/sdk"]');
        existingScripts.forEach(s => s.remove());
        window.paypal = undefined;
        
        // Load PayPal SDK with appropriate intent
        const script = document.createElement('script');
        const clientId = 'ATtOAGgoUaBRiQSclDhG6I7ER_KhNPgWGs3WUJYgs1fIUw4htpDW0d8NRCzehPkLxTNNBorisya_-NaK';
        
        if (neededSdkType === 'onetime') {
          script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
        } else {
          script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
        }
        
        script.async = true;
        script.onload = () => {
          setPaypalSdkType(neededSdkType);
          setPaypalLoading(false);
          renderPayPalButton();
        };
        script.onerror = () => {
          setPaypalLoading(false);
          console.error('Failed to load PayPal SDK');
        };
        document.body.appendChild(script);
      } else if (window.paypal) {
        // SDK already loaded with correct type, just re-render button
        renderPayPalButton();
      }
    }
  }, [showPaywall, paymentComplete, businessInfo.email, neededSdkType, paypalSdkType]);

  // Re-render button when plan changes within same SDK type (quarterly <-> annual) or when discount applied
  useEffect(() => {
    if (showPaywall && !paymentComplete && window.paypal && paypalSdkType === neededSdkType && !paypalLoading) {
      renderPayPalButton();
    }
  }, [selectedPlan, discountedPrice]);

  const renderPayPalButton = () => {
    if (!paypalRef.current || !window.paypal) return;
    
    paypalRef.current.innerHTML = '';
    
    // Subscription Plan IDs
    const subscriptionPlanIds = {
      'quarterly': 'P-8KM41655M19557031NFO7YKY',
      'annual': 'P-3CG228805F156324BNFO737I'
    };
    
    if (selectedPlan === 'onetime') {
      // One-time payment button
      window.paypal.Buttons({
        style: {
          layout: 'vertical',
          color: 'gold',
          shape: 'pill',
          label: 'paypal',
          height: 45
        },
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              description: 'BreachBlockIQ™ - Security IQ Test Report',
              amount: {
                value: discountedPrice.toFixed(2)
              }
            }]
          });
        },
        onApprove: async (data, actions) => {
          const order = await actions.order.capture();
          console.log('Payment successful:', order);
          console.log('Customer email:', businessInfo.email);
          console.log('Business name:', businessInfo.name);
          console.log('Plan:', 'onetime');
          console.log('Transaction ID:', order.id);
          setPaymentComplete(true);
          setShowPaywall(false);
          generateReport();
        },
        onError: (err) => {
          console.error('PayPal error:', err);
          alert('Payment failed. Please try again.');
        }
      }).render(paypalRef.current);
    } else {
      // Subscription button (quarterly or annual)
      window.paypal.Buttons({
        style: {
          shape: 'pill',
          color: 'gold',
          layout: 'vertical',
          label: 'subscribe',
          height: 45
        },
        createSubscription: (data, actions) => {
          return actions.subscription.create({
            plan_id: subscriptionPlanIds[selectedPlan]
          });
        },
        onApprove: (data, actions) => {
          console.log('Subscription successful!');
          console.log('Subscription ID:', data.subscriptionID);
          console.log('Customer email:', businessInfo.email);
          console.log('Business name:', businessInfo.name);
          console.log('Plan:', selectedPlan);
          setPaymentComplete(true);
          setShowPaywall(false);
          generateReport();
        },
        onError: (err) => {
          console.error('PayPal subscription error:', err);
          alert('Subscription failed. Please try again.');
        }
      }).render(paypalRef.current);
    }
  };

  const categories = [
    {
      id: 'data',
      title: 'Data Protection',
      icon: '🛡️',
      questions: [
        { 
          id: 'data_inventory', 
          text: 'Do you know where all your sensitive customer/client data is stored?', 
          helper: 'This includes things like customer names, addresses, payment info, health records, or any personal information.',
          options: ['Yes, fully documented', 'Partially, we have a general idea', 'No, data is scattered across systems', 'Not sure'] 
        },
        { 
          id: 'data_encryption', 
          text: 'Is sensitive data encrypted when stored and transmitted?', 
          helper: 'Encryption scrambles data so only authorized people can read it. Think of it like a secret code that protects information if someone steals your files.',
          options: ['Yes, all sensitive data', 'Some data is encrypted', 'No encryption in place', 'Not sure what this means'] 
        },
        { 
          id: 'data_retention', 
          text: 'Do you have a policy for how long you keep customer data?', 
          helper: 'A retention policy defines when old records should be deleted. Keeping data forever increases risk if there\'s ever a breach.',
          options: ['Yes, documented and followed', 'Informal guidelines exist', 'No policy, we keep everything', 'Not sure'] 
        },
        { 
          id: 'data_disposal', 
          text: 'How do you dispose of old computers, hard drives, and documents with sensitive info?', 
          helper: 'Simply deleting files doesn\'t actually remove them. Data can be recovered from "erased" drives with free software.',
          options: ['Professional destruction/wiping', 'We delete files and recycle', 'No formal process', 'Not sure'] 
        },
        { 
          id: 'data_classification', 
          text: 'Do you classify data by sensitivity level (public, internal, confidential)?', 
          helper: 'Data classification helps you know what needs the most protection. Not all data requires the same security level.',
          options: ['Yes, formal classification system', 'Informal understanding of what\'s sensitive', 'No classification', 'Not sure what this means'] 
        }
      ]
    },
    {
      id: 'access',
      title: 'Passwords & Access',
      icon: '🔐',
      questions: [
        { 
          id: 'password_policy', 
          text: 'Does your team (staff and volunteers) use unique, strong passwords for work accounts?', 
          helper: 'Strong passwords are at least 12 characters with a mix of letters, numbers, and symbols. Password managers like LastPass or 1Password make this easy.',
          options: ['Yes, enforced by policy/software', 'Encouraged but not enforced', 'No requirements', 'Not sure'] 
        },
        { 
          id: 'mfa', 
          text: 'Is multi-factor authentication (MFA) enabled on critical systems?', 
          helper: 'Multi-factor authentication (MFA) adds a second step when logging in, like a code texted to your phone or from an app. Even if someone steals your password, they can\'t get in without that second code. Most email and software now offer this for free.',
          options: ['Yes, on all critical systems', 'On some systems', 'No MFA in place', 'Not sure what MFA is'] 
        },
        { 
          id: 'access_reviews', 
          text: 'When someone leaves your organization, how quickly is their access removed?', 
          helper: 'This includes staff and volunteers. Former team members with active logins are a security risk, especially those with access to donor data or financial systems.',
          options: ['Same day, documented process', 'Within a week', 'Eventually, no formal process', 'Not sure'] 
        },
        { 
          id: 'shared_accounts', 
          text: 'Do multiple people share login credentials for any systems?', 
          helper: 'Common with volunteer teams sharing a social media or check-in login. Shared logins mean you can\'t tell who did what, and passwords must change for everyone when one person leaves.',
          options: ['No, everyone has individual accounts', 'Yes, for a few systems', 'Yes, commonly', 'Not sure'] 
        },
        { 
          id: 'admin_access', 
          text: 'Is administrator access limited to only those who truly need it?', 
          helper: 'Admin accounts can do anything: install software, change settings, access all files. The fewer people with admin rights, the lower your risk.',
          options: ['Yes, strictly limited', 'Somewhat limited', 'Most people have admin access', 'Not sure'] 
        }
      ]
    },
    {
      id: 'backup',
      title: 'Backups & Recovery',
      icon: '💾',
      questions: [
        { 
          id: 'backup_exists', 
          text: 'Do you regularly back up important business data?', 
          helper: 'Backups are copies of your important files stored separately, so you can recover if something goes wrong.',
          options: ['Yes, automated daily/weekly', 'Yes, but manually/irregularly', 'No backups', 'Not sure'] 
        },
        { 
          id: 'backup_tested', 
          text: 'Have you ever tested restoring data from a backup?', 
          helper: 'Many businesses discover their backups don\'t work only when they desperately need them. Testing means actually trying to restore a file.',
          options: ['Yes, we test regularly', 'Yes, tested once', 'Never tested', 'Not sure'] 
        },
        { 
          id: 'backup_offsite', 
          text: 'Are backups stored in a separate location (cloud or offsite)?', 
          helper: 'If backups are on the same computer or in the same building, a fire, flood, or ransomware attack (where hackers lock your files and demand payment) could destroy both the original and the backup.',
          options: ['Yes, in the cloud or offsite', 'No, same location as original', 'Not sure'] 
        },
        { 
          id: 'disaster_plan', 
          text: 'Do you have a plan for getting back to work if your systems went down tomorrow?', 
          helper: 'A disaster recovery plan outlines exactly what to do, who to call, and how to get back up and running.',
          options: ['Yes, documented plan', 'Informal idea of what to do', 'No plan', 'Not sure'] 
        },
        { 
          id: 'backup_frequency', 
          text: 'How often are your most critical files backed up?', 
          helper: 'If you only backup weekly, you could lose up to a week of work. Consider how much data loss your business can tolerate.',
          options: ['Daily or more frequently', 'Weekly', 'Monthly or less', 'Not sure'] 
        }
      ]
    },
    {
      id: 'training',
      title: 'Staff Awareness',
      icon: '👥',
      questions: [
        { 
          id: 'security_training', 
          text: 'Does your team (staff and volunteers) receive any cybersecurity awareness training?', 
          helper: 'Most breaches start with human error, such as clicking a bad link, using weak passwords, or falling for scams. Training helps prevent this.',
          options: ['Yes, regular training', 'Yes, during onboarding only', 'No formal training', 'Not applicable (solo operation)'] 
        },
        { 
          id: 'phishing_awareness', 
          text: 'Would your team know how to recognize a phishing email?', 
          helper: 'Phishing emails pretend to be from legitimate companies to trick you into clicking malicious links or giving up passwords. They\'re increasingly sophisticated.',
          options: ['Yes, we train on this specifically', 'Probably, most are tech-savvy', 'Probably not', 'Not sure'] 
        },
        { 
          id: 'incident_reporting', 
          text: 'Does your team know who to contact if they suspect a security issue?', 
          helper: 'Quick reporting can be the difference between a minor issue and a major breach. Everyone needs a clear person to call.',
          options: ['Yes, clear process exists', 'They would figure it out', 'No designated contact', 'Not sure'] 
        },
        { 
          id: 'policy_documentation', 
          text: 'Do you have written security policies your team can reference?', 
          helper: 'Written policies set clear expectations and give people something to refer back to. They are also often required for compliance.',
          options: ['Yes, documented and accessible', 'Some informal guidelines', 'No written policies', 'Not sure'] 
        },
        { 
          id: 'payment_verification', 
          text: 'Do you have a process to verify payment or wire transfer requests?', 
          helper: 'Wire fraud targeting organizations has increased 300%+ since 2020. Attackers impersonate vendors, leadership, or trusted contacts to redirect payments. A verification call to a known number stops most attempts.',
          options: ['Yes, mandatory verification process', 'Sometimes, depends on amount', 'No formal process', 'Not sure'] 
        }
      ]
    },
    {
      id: 'compliance',
      title: 'Regulatory Awareness',
      icon: '📋',
      questions: [
        { 
          id: 'hipaa', 
          text: 'Do you handle any health information (patient records, insurance info)?', 
          helper: 'If yes, HIPAA (Health Insurance Portability and Accountability Act) regulations likely apply to your organization, with specific requirements for protecting health data.',
          options: ['Yes, regularly', 'Occasionally', 'No', 'Not sure'] 
        },
        { 
          id: 'pci', 
          text: 'Do you accept credit card payments?', 
          helper: 'Businesses that process credit cards must follow PCI-DSS (Payment Card Industry Data Security Standard). Using a third-party processor like Square or Stripe handles most of this for you.',
          options: ['Yes, we process cards', 'Yes, but through a third party only', 'No', 'Not sure'] 
        },
        { 
          id: 'pii', 
          text: 'Do you collect sensitive personal information (Social Security Numbers, financial data, children\'s info)?', 
          helper: 'This includes customer/client payment info, background check data, children\'s records, or any personal details. This data is heavily regulated and a prime target for thieves.',
          options: ['Yes, regularly', 'Occasionally', 'No', 'Not sure'] 
        },
        { 
          id: 'vendor_security', 
          text: 'Do you know if your software vendors follow security best practices?', 
          helper: (() => {
            const softwareByIndustry = {
              'Religious Organization': 'Planning Center, Breeze, Church Community Builder, Pushpay, Realm',
              'Daycare / Childcare': 'Brightwheel, Procare, HiMama, Kangarootime, Childcare Manager',
              'Private School': 'Blackbaud, FACTS, PowerSchool, Gradelink, RenWeb',
              'Nonprofit / Association': 'Bloomerang, Little Green Light, Neon, Salsa, Network for Good',
              'Healthcare / Medical': 'Epic, Cerner, athenahealth, eClinicalWorks, Practice Fusion',
              'Financial Services': 'Redtail, Wealthbox, Orion, Black Diamond, Junxure',
              'Retail / E-commerce': 'Shopify, Square, Lightspeed, Vend, Toast',
              'Professional Services': 'Clio, PracticePanther, MyCase, Rocket Matter, TimeSolv',
              'IT / Technology Services': 'ConnectWise, Datto, Autotask, NinjaOne, Syncro',
              'Education': 'Canvas, Blackboard, PowerSchool, Infinite Campus, Skyward',
              'Construction': 'Procore, Buildertrend, CoConstruct, JobNimbus, Contractor Foreman',
              'Trades & Repair Services': 'ServiceTitan, Housecall Pro, Jobber, FieldEdge, Service Fusion',
              'Personal Services': 'Vagaro, Mindbody, Fresha, Square Appointments, Booksy',
              'Funeral Services': 'Passare, FrontRunner, SRS Computing, Halcyon, FuneralTech',
              'Manufacturing / Warehouse': 'Fishbowl, Katana, NetSuite, SAP Business One, Cin7',
              'Restaurant / Hospitality': 'Toast, Square for Restaurants, TouchBistro, Lightspeed, Revel',
              'Real Estate': 'Follow Up Boss, BoomTown, LionDesk, Dotloop, Wise Agent'
            };
            const software = softwareByIndustry[businessInfo.industry] || 'your management software, accounting software';
            return `This includes systems like ${software}, email providers, and any cloud services. If they get breached, your data may be exposed.`;
          })(),
          options: ['Yes, we vet vendors', 'We assume they do', 'Never considered this', 'Not sure'] 
        },
        { 
          id: 'cyber_insurance', 
          text: 'Do you have cyber liability insurance?', 
          helper: 'Cyber insurance covers breach costs like legal fees, notification requirements, and recovery expenses. Many insurance providers now offer cyber add-ons. Check with your carrier.',
          options: ['Yes, active policy', 'Included in general liability', 'No cyber coverage', 'Not sure'] 
        }
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const calculateRiskScore = () => {
    let highRisk = 0;
    let mediumRisk = 0;
    let iqScore = 0;
    
    Object.entries(answers).forEach(([key, value]) => {
      if (value.includes('No ') || value.includes('Never') || value === 'Not sure' || value.includes('Not sure what') || value.includes('No encryption') || value.includes('No backups') || value.includes('No training') || value.includes('No plan') || value.includes('No formal') || value.includes('No policy') || value.includes('No classification') || value.includes('No cyber') || value.includes('No designated')) {
        highRisk++;
        iqScore += 0;
      } else if (value.includes('Partially') || value.includes('Informal') || value.includes('Occasionally') || value.includes('some') || value.includes('Eventually') || value.includes('Rarely') || value.includes('Once or twice') || value.includes('General idea') || value.includes('Somewhat') || value.includes('Sometimes') || value.includes('Weekly') || value.includes('assume') || value.includes('Included in general')) {
        mediumRisk++;
        iqScore += 12;
      } else {
        iqScore += 20;
      }
    });
    
    return { 
      highRisk, 
      mediumRisk, 
      lowRisk: Object.keys(answers).length - highRisk - mediumRisk,
      iqScore: iqScore
    };
  };

  // Send report email function
  const sendReportEmail = async (emailAddress, reportData) => {
    console.log('sendReportEmail called with:', emailAddress);
    
    if (!emailAddress || !emailAddress.includes('@')) {
      console.log('No valid email provided for report delivery');
      return;
    }
    
    try {
      // Check if EmailJS is loaded
      if (typeof window.emailjs === 'undefined') {
        console.error('EmailJS not loaded - email will not be sent');
        alert('Note: Email could not be sent. Please download your report to save it.');
        return;
      }
      
      console.log('EmailJS is loaded, preparing to send to:', emailAddress);
      
      const r = reportData || {};
      const { iqScore } = calculateRiskScore();
      
      // Build priorities section
      let prioritiesText = '';
      if (r.priorities && r.priorities.length > 0) {
        prioritiesText = r.priorities.map((p, i) => 
          `${i + 1}. ${p.title || 'Priority'}\n   → ${p.reason || ''}`
        ).join('\n\n');
      }
      
      // Build critical issues section
      let criticalText = '';
      if (r.criticalIssues && r.criticalIssues.length > 0) {
        criticalText = r.criticalIssues.map(issue => 
          `⚠️ ${issue.topic || 'Issue'}\n   Risk: ${issue.risk || 'See recommendation'}\n   Fix: ${issue.fix || 'Address this issue'}\n   Effort: ${issue.effort || 'Varies'}`
        ).join('\n\n');
      }
      
      // Build attention items section
      let attentionText = '';
      if (r.attentionIssues && r.attentionIssues.length > 0) {
        attentionText = r.attentionIssues.map(issue => 
          `• ${issue.topic || 'Issue'}\n   Fix: ${issue.fix || 'Address this'}\n   Effort: ${issue.effort || 'Varies'}`
        ).join('\n\n');
      }
      
      // Build good practices section
      let goodText = '';
      if (r.goodPractices && r.goodPractices.length > 0) {
        goodText = r.goodPractices.map(practice => `✓ ${practice}`).join('\n');
      }
      
      // Build action plan section
      let actionText = '';
      if (r.actionPlan) {
        const week1 = r.actionPlan.week1 ? r.actionPlan.week1.join('\n   • ') : '';
        const week2 = r.actionPlan.week2to4 ? r.actionPlan.week2to4.join('\n   • ') : '';
        const ongoing = r.actionPlan.ongoing ? r.actionPlan.ongoing.join('\n   • ') : '';
        actionText = `WEEK 1 (Quick Wins):\n   • ${week1}\n\nWEEK 2-4:\n   • ${week2}\n\nONGOING:\n   • ${ongoing}`;
      }
      
      // Build full report content
      const fullReport = `
═══════════════════════════════════════
       YOUR BREACHBLOCKIQ™ REPORT
═══════════════════════════════════════

Organization: ${businessInfo.name || 'Your Organization'}
Industry: ${businessInfo.industry || 'General'}
Date: ${new Date().toLocaleDateString()}

───────────────────────────────────────
SCORE: ${r.score || iqScore}/500 - ${r.riskLevel || 'PENDING'}
───────────────────────────────────────

Critical Issues: ${r.criticalCount || 0}
Needs Attention: ${r.attentionCount || 0}
Good Practices: ${r.goodCount || 0}

═══════════════════════════════════════
EXECUTIVE SUMMARY
═══════════════════════════════════════

${r.executiveSummary || 'Assessment completed.'}

═══════════════════════════════════════
TOP 3 PRIORITIES
═══════════════════════════════════════

${prioritiesText || 'See full report for priorities.'}

═══════════════════════════════════════
CRITICAL ISSUES (${r.criticalCount || 0})
═══════════════════════════════════════

${criticalText || 'No critical issues identified.'}

═══════════════════════════════════════
NEEDS ATTENTION (${r.attentionCount || 0})
═══════════════════════════════════════

${attentionText || 'No attention items identified.'}

═══════════════════════════════════════
WHAT YOU'RE DOING WELL (${r.goodCount || 0})
═══════════════════════════════════════

${goodText || 'See full report.'}

═══════════════════════════════════════
30-DAY ACTION PLAN
═══════════════════════════════════════

${actionText || 'See full report for action plan.'}

───────────────────────────────────────

This is an educational tool providing guidance based on your self-reported answers. It is not a security audit, compliance certification, or legal advice.

Need help implementing? Contact TechShield KC:
www.techshieldkc.com | info@techshieldkc.com

© ${new Date().getFullYear()} BreachBlockIQ™ | TechShield KC LLC
`;

      const templateParams = {
        to_email: emailAddress,
        to_name: businessInfo.name || 'Customer',
        from_name: 'BreachBlockIQ',
        iq_score: r.score || iqScore,
        risk_level: r.riskLevel || 'PENDING',
        critical_issues: r.criticalCount || 0,
        attention_items: r.attentionCount || 0,
        good_practices: r.goodCount || 0,
        industry: businessInfo.industry || 'General',
        organization: businessInfo.name || 'Your Organization',
        report_summary: r.executiveSummary || 'Your security assessment is complete.',
        full_report: fullReport
      };
      
      console.log('Sending email with full report...');
      
      const result = await window.emailjs.send(
        'service_9hdzso6',
        'template_n2flukf',
        templateParams
      );
      
      console.log('Email sent successfully:', result);
    } catch (error) {
      console.error('Failed to send report email:', error);
      // Don't alert - email is bonus, not required. User has the PDF.
    }
  };

  // Background report generation - starts when entering last category
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [pendingReport, setPendingReport] = useState(null);

  const generateReport = async () => {
    // If report already generated in background, use it immediately
    if (reportReady && pendingReport) {
      setLoading(false);
      setReport(pendingReport);
      setCurrentStep('report');
      
      // Send email now (user just entered their email on paywall)
      if (businessInfo.email) {
        sendReportEmail(businessInfo.email, pendingReport);
      }
      return;
    }
    
    // Show loading screen - the useEffect will transition to report when ready
    setLoading(true);
    setShowPaywall(false);
    
    // If not already generating, start the API call
    if (!reportGenerating) {
      await generateReportAPI();
    }
    // If already generating in background, useEffect will handle transition when reportReady becomes true
  };

  const generateReportAPI = async () => {
    setReportGenerating(true);
    
    // Small delay to let loading screen render before API call
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const prompt = `You are a cybersecurity advisor. Analyze this security assessment and return ONLY valid JSON.

BUSINESS: ${businessInfo.name}
INDUSTRY: ${businessInfo.industry}
SIZE: ${businessInfo.size}

THEIR ANSWERS:
${Object.entries(answers).map(([q, a]) => `${q}: ${a}`).join('\n')}

SCORING RULES:
- 25 questions × 20 points each = 500 max
- Best practice answer = 20 points
- Partial/some protection = 12 points
- No protection/poor practice = 0 points
- Score ranges: Under 200=HIGH RISK, 200-300=ELEVATED RISK, 300-400=MODERATE, 400-500=STRONG

INDUSTRY-SPECIFIC CONTEXT (use stats relevant to their industry):
- Religious Organizations: Wire fraud targeting churches up 300% since 2020, avg loss $84K. Donor data theft, volunteer access sprawl, child safety data (background checks), benevolence fund fraud.
- Daycare / Childcare: Children's PII highest value on dark web. Parent financial data, licensing compliance risks, staff turnover creates access issues, custody disputes create physical security needs.
- Private School: Student records (FERPA), tuition payment data, staff background checks, parent portal vulnerabilities, endowment/donation data.
- Nonprofit / Association: Donor databases prime targets, grant fraud, volunteer access management, public tax returns expose financials, advocacy orgs face activist threats.
- Healthcare / Medical: HIPAA fines up to $50K/record, 21-day average ransomware downtime, patient data sells for $250+/record, medical device vulnerabilities.
- Financial Services: SEC/FINRA compliance, wire fraud, client account takeover, insider threats, average breach cost $5.9M in finance sector.
- Retail / E-commerce: PCI-DSS compliance required, 24% of breaches target retail, POS malware, gift card fraud, inventory system ransomware.
- Professional Services: Client confidentiality paramount, 60% of clients leave firm after breach, email compromise for invoice fraud, M&A data theft.
- IT / Technology Services: MSPs are prime targets (access to many clients), supply chain attacks, software update poisoning, 76% of MSPs attacked in past 2 years.
- Education: FERPA compliance, student data valuable, ransomware doubled in education sector, state-sponsored research theft, limited IT budgets.
- Construction: Blueprint theft, bid manipulation, progress payment fraud, subcontractor credential theft, project delay ransomware.
- Trades & Repair Services: Customer home access info, payment data, fleet/dispatch system attacks, warranty fraud, scheduling system ransomware.
- Personal Services: Appointment systems contain contact/payment data, HIPAA may apply (medical spa), reputation attacks, booking platform vulnerabilities.
- Funeral Services: Deceased PII prime target for identity theft (criminals exploit that deceased don't monitor credit), family financial data, pre-need contract fraud, state vital records compliance, 24/7 operations create remote access risks, grieving families targeted for wire fraud, obituary systems can expose family info.
- Manufacturing / Warehouse: Industrial control system attacks, IP theft, supply chain disruption, inventory manipulation, safety system tampering.
- Restaurant / Hospitality: POS systems (PCI compliance), reservation data, loyalty program fraud, third-party delivery app vulnerabilities.
- Real Estate: Wire fraud on closings ($150K avg loss), MLS data, client financial info, electronic signature fraud, title insurance scams.

Return this JSON structure with CONCISE, ACTIONABLE content:
{
  "score": [calculated number 0-500],
  "riskLevel": "[HIGH RISK/ELEVATED RISK/MODERATE/STRONG]",
  "criticalCount": [number of critical issues],
  "attentionCount": [number of attention items],
  "goodCount": [number of good practices],
  "executiveSummary": "[2-3 sentences max: Key strengths, main vulnerabilities, bottom line. Be direct.]",
  "priorities": [
    {"title": "[Specific action]", "reason": "[One sentence why]"},
    {"title": "[Action]", "reason": "[Why]"},
    {"title": "[Action]", "reason": "[Why]"}
  ],
  "criticalIssues": [
    {
      "topic": "[Specific topic from their weak answer]",
      "answer": "[Quote their actual answer]",
      "risk": "[1 sentence: specific consequence for their industry]",
      "fix": "[1-2 sentences: exactly what to do and what tool to use]",
      "effort": "[Time estimate]"
    }
  ],
  "attentionIssues": [
    {
      "topic": "[Topic]",
      "answer": "[Their answer]",
      "fix": "[1 sentence: specific improvement]",
      "effort": "[Time estimate]"
    }
  ],
  "goodPractices": [
    "[Short phrase describing what they're doing well]"
  ],
  "actionPlan": {
    "week1": ["[Quick win]", "[Quick win]"],
    "week2to4": ["[Larger task]", "[Task]"],
    "ongoing": ["[Maintenance task]"]
  }
}

REQUIREMENTS:
- Return ONLY valid JSON, no markdown or explanation
- BE CONCISE. Risk = 1 sentence. Fix = 1-2 sentences max. No fluff.
- Be specific to ${businessInfo.industry} industry
- Reference their actual answers
- Critical = serious gaps, Attention = improvements needed, Good = strong answers
- Actionable with specific tools/steps`;


    try {
      const response = await fetch('/api/generate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate report');
      }
      
      // Parse the JSON response from AI
      let reportData;
      try {
        const reportText = data.report || '';
        // Clean up any markdown code blocks if present
        const cleanJson = reportText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        reportData = JSON.parse(cleanJson);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.log('Raw response:', data.report);
        // Create fallback data
        reportData = {
          score: 250,
          riskLevel: 'ELEVATED RISK',
          riskSummary: 'Assessment completed. See details below',
          criticalCount: 5,
          attentionCount: 10,
          goodCount: 10,
          executiveSummary: 'Your security assessment has been completed. Please review the findings below.',
          priorities: [
            { title: 'Review Critical Issues', reason: 'Address the most urgent security gaps first' },
            { title: 'Implement Quick Wins', reason: 'Low-effort changes that improve security immediately' },
            { title: 'Document Procedures', reason: 'Written policies help ensure consistent security practices' }
          ],
          criticalIssues: [],
          attentionIssues: [],
          goodPractices: ['Assessment completed'],
          actionPlan: { week1: ['Review this report'], week2to4: ['Address critical issues'], ongoing: ['Regular security reviews'] }
        };
      }
      
      // Store report data
      setPendingReport(reportData);
      setReportReady(true);
      setReportGenerating(false);
      
      // Send email with report
      if (businessInfo.email) {
        sendReportEmail(businessInfo.email, reportData);
      }
      
      // If user is waiting (loading screen), show report immediately
      if (loading) {
        setReport(reportData);
        setCurrentStep('report');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      // FALLBACK: Generate report client-side if API fails
      const fallbackReport = generateClientSideReport(answers, businessInfo);
      setPendingReport(fallbackReport);
      setReportReady(true);
      setReportGenerating(false);
      
      if (loading) {
        setReport(fallbackReport);
        setCurrentStep('report');
        setLoading(false);
      }
    }
  };

  // Client-side scoring fallback function
  const generateClientSideReport = (answers, businessInfo) => {
    // Scoring based on ACTUAL question IDs and options
    const scoringRules = {
      // Data Protection
      'data_inventory': { best: ['Yes, fully documented'], partial: ['Partially, we have a general idea'] },
      'data_encryption': { best: ['Yes, all sensitive data'], partial: ['Some data is encrypted'] },
      'data_retention': { best: ['Yes, documented and followed'], partial: ['Informal guidelines exist'] },
      'data_disposal': { best: ['Professional destruction/wiping'], partial: ['We delete files and recycle'] },
      'data_classification': { best: ['Yes, formal classification system'], partial: ["Informal understanding of what's sensitive"] },
      // Passwords & Access
      'password_policy': { best: ['Yes, enforced by policy/software'], partial: ['Encouraged but not enforced'] },
      'mfa': { best: ['Yes, on all critical systems'], partial: ['On some systems'] },
      'access_reviews': { best: ['Same day, documented process'], partial: ['Within a week'] },
      'shared_accounts': { best: ['No, everyone has individual accounts'], partial: ['Yes, for a few systems'] },
      'admin_access': { best: ['Yes, strictly limited'], partial: ['Somewhat limited'] },
      // Backups & Recovery
      'backup_exists': { best: ['Yes, automated daily/weekly'], partial: ['Yes, but manually/irregularly'] },
      'backup_tested': { best: ['Yes, we test regularly'], partial: ['Yes, tested once'] },
      'backup_offsite': { best: ['Yes, in the cloud or offsite'], partial: [] },
      'disaster_plan': { best: ['Yes, documented plan'], partial: ['Informal idea of what to do'] },
      'backup_frequency': { best: ['Daily or more frequently'], partial: ['Weekly'] },
      // Staff Awareness
      'security_training': { best: ['Yes, regular training'], partial: ['Yes, during onboarding only', 'Not applicable (solo operation)'] },
      'phishing_awareness': { best: ['Yes, we train on this specifically'], partial: ['Probably, most are tech-savvy'] },
      'incident_reporting': { best: ['Yes, clear process exists'], partial: ['They would figure it out'] },
      'policy_documentation': { best: ['Yes, documented and accessible'], partial: ['Some informal guidelines'] },
      'payment_verification': { best: ['Yes, mandatory verification process'], partial: ['Sometimes, depends on amount'] },
      // Regulatory Awareness
      'hipaa': { best: ['No'], partial: ['Occasionally'] },
      'pci': { best: ['No', 'Yes, but through a third party only'], partial: ['Yes, we process cards'] },
      'pii': { best: ['No'], partial: ['Occasionally'] },
      'vendor_security': { best: ['Yes, we vet vendors'], partial: ['We assume they do'] },
      'cyber_insurance': { best: ['Yes, active policy'], partial: ['Included in general liability'] }
    };

    // Topic labels for questions
    const topicLabels = {
      'data_inventory': 'Data Inventory',
      'data_encryption': 'Data Encryption',
      'data_retention': 'Data Retention Policy',
      'data_disposal': 'Secure Data Disposal',
      'data_classification': 'Data Classification',
      'password_policy': 'Password Policy',
      'mfa': 'Multi-Factor Authentication',
      'access_reviews': 'Access Removal Process',
      'shared_accounts': 'Shared Account Usage',
      'admin_access': 'Admin Access Controls',
      'backup_exists': 'Backup System',
      'backup_tested': 'Backup Testing',
      'backup_offsite': 'Offsite Backup Storage',
      'disaster_plan': 'Disaster Recovery Plan',
      'backup_frequency': 'Backup Frequency',
      'security_training': 'Security Awareness Training',
      'phishing_awareness': 'Phishing Recognition',
      'incident_reporting': 'Incident Reporting Process',
      'policy_documentation': 'Written Security Policies',
      'payment_verification': 'Payment Verification Process',
      'hipaa': 'Health Data Handling',
      'pci': 'Payment Card Processing',
      'pii': 'Personal Information Handling',
      'vendor_security': 'Vendor Security Review',
      'cyber_insurance': 'Cyber Insurance Coverage'
    };

    // Fix suggestions
    const fixSuggestions = {
      'data_inventory': 'Document where all sensitive data is stored. Create a simple spreadsheet listing systems and what data they contain.',
      'data_encryption': 'Enable encryption on laptops (BitLocker/FileVault) and ensure your email uses TLS. Most cloud services encrypt by default.',
      'data_retention': 'Create a data retention schedule. Define how long to keep records and when to securely delete them.',
      'data_disposal': 'Use a professional data destruction service or certified wiping software. Never just delete and recycle.',
      'data_classification': 'Create simple categories: Public, Internal, Confidential. Label sensitive documents accordingly.',
      'password_policy': 'Require 12+ character passwords. Consider a password manager like 1Password or Bitwarden for your team.',
      'mfa': 'Enable MFA on email, financial systems, and admin accounts. Most platforms offer this free. Start with email today.',
      'access_reviews': 'Create an offboarding checklist. Include all systems that need access removed when someone leaves.',
      'shared_accounts': 'Create individual logins for each person. Most platforms allow multiple users at no extra cost.',
      'admin_access': 'Audit who has admin rights. Remove admin access from anyone who doesn\'t absolutely need it.',
      'backup_exists': 'Set up automated backups. Cloud services like Backblaze or built-in options like OneDrive/Google Drive work well.',
      'backup_tested': 'Schedule a quarterly test restore. Actually try recovering a file to make sure backups work.',
      'backup_offsite': 'Add cloud backup or store backup drives offsite. On-premise only backups can be lost to fire, flood, or ransomware.',
      'disaster_plan': 'Document what to do if systems go down: who to call, how to access backups, how to notify stakeholders.',
      'backup_frequency': 'Increase backup frequency to daily. Most backup solutions support this at no extra cost.',
      'security_training': 'Implement annual security awareness training. Free options include CISA resources or KnowBe4\'s free tools.',
      'phishing_awareness': 'Add phishing awareness training. Show examples of real phishing emails targeting your industry.',
      'incident_reporting': 'Designate a security contact. Post their info and create a simple "if you see something, say something" process.',
      'policy_documentation': 'Create a 1-2 page acceptable use policy. Templates are available free from SANS or NIST.',
      'payment_verification': 'Require phone verification for all wire transfers using a known number, not one from the email request.',
      'hipaa': 'If you handle health data, ensure HIPAA compliance: encryption, access controls, BAAs with vendors.',
      'pci': 'If processing cards directly, ensure PCI compliance. Using a third-party processor greatly simplifies this.',
      'pii': 'Minimize PII collection. Only collect what you need and secure it with encryption and access controls.',
      'vendor_security': 'Ask key vendors about their security practices. Request SOC 2 reports or security documentation.',
      'cyber_insurance': 'Contact your insurance agent about cyber coverage. Typical cost: $500-1,500/year for small organizations.'
    };

    let score = 0;
    const criticalIssues = [];
    const attentionIssues = [];
    const goodPractices = [];

    // Process each answer
    Object.entries(answers).forEach(([questionId, answer]) => {
      const rules = scoringRules[questionId];
      const topic = topicLabels[questionId] || questionId;
      const fix = fixSuggestions[questionId] || 'Review and improve this area.';
      
      if (!rules) {
        console.log('No scoring rule for:', questionId);
        return;
      }

      // Check if answer matches best practice
      const isBest = rules.best && rules.best.some(opt => answer === opt);
      const isPartial = rules.partial && rules.partial.some(opt => answer === opt);

      if (isBest) {
        score += 20;
        goodPractices.push(topic);
      } else if (isPartial) {
        score += 12;
        attentionIssues.push({
          topic: topic,
          answer: answer,
          fix: fix,
          effort: '1-4 hours'
        });
      } else {
        score += 0;
        criticalIssues.push({
          topic: topic,
          answer: answer,
          risk: 'This gap may expose your organization to preventable security incidents.',
          fix: fix,
          effort: '1-4 hours'
        });
      }
    });

    // Determine risk level
    let riskLevel;
    if (score < 200) riskLevel = 'HIGH RISK';
    else if (score < 300) riskLevel = 'ELEVATED RISK';
    else if (score < 400) riskLevel = 'MODERATE';
    else riskLevel = 'STRONG';

    // Generate priorities from critical issues
    const priorities = criticalIssues.slice(0, 3).map(issue => ({
      title: 'Address ' + issue.topic,
      reason: issue.fix
    }));
    
    if (priorities.length === 0 && attentionIssues.length > 0) {
      priorities.push(...attentionIssues.slice(0, 3).map(issue => ({
        title: 'Improve ' + issue.topic,
        reason: issue.fix
      })));
    }
    
    if (priorities.length === 0) {
      priorities.push({ title: 'Maintain Current Practices', reason: 'Your security posture is solid. Focus on continuous improvement.' });
    }

    // Generate action plan
    const week1Tasks = criticalIssues.slice(0, 3).map(i => 'Address ' + i.topic);
    const week2Tasks = criticalIssues.slice(3).concat(attentionIssues.slice(0, 3)).map(i => 'Improve ' + i.topic);
    
    const actionPlan = {
      week1: week1Tasks.length > 0 ? week1Tasks : ['Review report findings', 'Prioritize improvements'],
      week2to4: week2Tasks.length > 0 ? week2Tasks : ['Continue security improvements', 'Document current practices'],
      ongoing: ['Quarterly security reviews', 'Annual training updates', 'Regular backup testing']
    };

    // Generate executive summary
    const execSummary = `Based on your responses, your organization scored ${score} out of 500 points (${riskLevel}). ` +
      `You have ${goodPractices.length} security practices in place` +
      (criticalIssues.length > 0 ? `, ${criticalIssues.length} areas that need immediate attention` : '') +
      (attentionIssues.length > 0 ? `, and ${attentionIssues.length} items worth reviewing` : '') + '. ' +
      (criticalIssues.length > 0 ? `Priority: Address ${criticalIssues[0]?.topic || 'critical gaps'} first.` : 'Continue maintaining your strong security posture.');

    return {
      score: score,
      riskLevel: riskLevel,
      criticalCount: criticalIssues.length,
      attentionCount: attentionIssues.length,
      goodCount: goodPractices.length,
      executiveSummary: execSummary,
      priorities: priorities,
      criticalIssues: criticalIssues,
      attentionIssues: attentionIssues,
      goodPractices: goodPractices,
      actionPlan: actionPlan
    };
  };

  // Start background generation when entering last category (category 4, index 4)
  useEffect(() => {
    if (currentCategory === 4 && !reportGenerating && !reportReady && Object.keys(answers).length >= 20) {
      console.log('Starting background report generation...');
      generateReportAPI();
    }
  }, [currentCategory, answers]);

  // When background report is ready and user is waiting, show it
  useEffect(() => {
    if (loading && reportReady && pendingReport) {
      console.log('Background report ready, showing to user...');
      setReport(pendingReport);
      setCurrentStep('report');
      setLoading(false);
      
      // Send email now (user entered email before loading screen)
      if (businessInfo.email) {
        sendReportEmail(businessInfo.email, pendingReport);
      }
    }
  }, [loading, reportReady, pendingReport]);

  const currentCategoryData = categories[currentCategory];
  const allQuestionsAnswered = currentCategoryData?.questions.every(q => answers[q.id]);
  const totalQuestions = categories.reduce((sum, cat) => sum + cat.questions.length, 0);
  const answeredQuestions = Object.keys(answers).length;

  const baseStyles = {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.darkBg} 0%, ${colors.black} 100%)`,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: colors.white,
    padding: 'clamp(1rem, 4vw, 2rem)',
    overflowX: 'hidden'
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { overflow-x: hidden; }
    html { font-size: 16px; }
    body { -webkit-text-size-adjust: 100%; }
    input, select, button { font-family: inherit; font-size: 16px; }
    input, select { min-height: 48px; }
    input[type="email"], input[type="text"] { -webkit-appearance: none; appearance: none; }
    button { min-height: 48px; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
    ::selection { background: ${colors.orange}; color: white; }
    @media (max-width: 480px) {
      html { font-size: 18px; }
    }
  `;

  // Intro Screen - Landing Page v2
  if (currentStep === 'intro') {
    const audiences = {
      business: { label: 'Business', stat: '43% of cyberattacks target small businesses', subtext: "yet most don't know where they're vulnerable" },
      church: { label: 'Churches', stat: '73% of churches have no cybersecurity policy', subtext: 'yet handle sensitive donor data daily' },
      school: { label: 'Schools', stat: '1,600+ cyberattacks hit schools weekly', subtext: 'with student data at constant risk' },
      nonprofit: { label: 'Nonprofits', stat: '68% of nonprofits have no cyber plan', subtext: 'putting donor trust at risk' }
    };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0a0a0a', color: '#e5e5e5', fontFamily: "'Inter', system-ui, sans-serif", lineHeight: 1.6 }}>
        <style>{globalStyles}</style>
        
        {/* Gradient */}
        <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(232,121,43,0.08) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          
          {/* Saved Report Banner */}
          {hasStoredReport && report && (
            <div style={{ 
              background: 'linear-gradient(135deg, #1a472a 0%, #0f2818 100%)', 
              borderBottom: '2px solid #22c55e',
              padding: '16px 24px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#fff', fontSize: '1rem', marginBottom: '12px' }}>
                📄 You have a saved report for <strong>{businessInfo.name}</strong>
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setCurrentStep('report')}
                  style={{
                    background: '#22c55e',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    color: '#000',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                >
                  View My Report
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem('breachblockiq_report');
                    localStorage.removeItem('breachblockiq_business');
                    setHasStoredReport(false);
                    setReport(null);
                    setBusinessInfo({ name: '', industry: '', size: '', email: '' });
                    setPaymentComplete(false);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid #666',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    color: '#999',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem'
                  }}
                >
                  Start Fresh
                </button>
              </div>
            </div>
          )}

          {/* Hero */}
          <section style={{ padding: 'clamp(30px, 8vw, 60px) 24px 48px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            
            <h1 style={{ fontSize: 'clamp(3.2rem, 15vw, 3.5rem)', fontWeight: 700, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              <span style={{ color: '#fff' }}>BreachBlock</span>
              <span style={{ color: colors.orange }}>IQ</span>
              <span style={{ color: colors.orange, fontSize: '0.5em', verticalAlign: 'super' }}>™</span>
            </h1>

            <p style={{ fontSize: '1.25rem', color: '#999', marginBottom: '8px' }}>
              Before you spend $5,000 on a consultant,
            </p>
            <p style={{ fontSize: '1.25rem', color: '#fff', marginBottom: '28px' }}>
              spend <span style={{ color: colors.orange, fontWeight: 600 }}>under 10 minutes</span> finding out what you actually need.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px', marginBottom: '8px', fontSize: '1.05rem', color: '#666' }}>
              <span>✓ ~10 min</span>
              <span>✓ 25 questions</span>
              <span>✓ NIST & CIS</span>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#444', marginBottom: '20px' }}>
              (National Institute of Standards and Technology & Center for Internet Security)
            </p>

            {/* Plain English callout */}
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.03)', 
              border: '1px solid #333', 
              borderRadius: '10px', 
              padding: '16px 20px', 
              marginBottom: '28px',
              maxWidth: '480px',
              margin: '0 auto 28px'
            }}>
              <p style={{ fontSize: '1rem', color: '#ccc', margin: 0, lineHeight: 1.7 }}>
                <span style={{ color: '#fff', fontWeight: 600 }}>No tech background needed.</span> Most security reports are 30-50 pages of technical language. Ours has <span style={{ color: colors.orange, fontWeight: 600 }}>75% less tech talk</span> so you know what matters <em>before</em> spending on IT.
              </p>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
              {Object.entries(audiences).map(([key, val]) => (
                <button key={key} onClick={() => setActiveTab(key)} style={{ padding: '10px 16px', fontSize: '0.95rem', fontWeight: 500, backgroundColor: activeTab === key ? colors.orange : 'transparent', color: activeTab === key ? '#000' : '#666', border: activeTab === key ? 'none' : '1px solid #333', borderRadius: '20px', cursor: 'pointer', fontFamily: 'inherit' }}>
                  {val.label}
                </button>
              ))}
            </div>

            {/* Stat box */}
            <div style={{ backgroundColor: 'rgba(232,121,43,0.08)', borderLeft: `4px solid ${colors.orange}`, borderRadius: '8px', padding: '20px 24px', textAlign: 'center', marginBottom: '32px' }}>
              <p style={{ fontSize: '1.15rem', margin: 0, lineHeight: 1.6 }}>
                <span style={{ color: colors.orange, fontWeight: 600 }}>{audiences[activeTab].stat}</span>
                <span style={{ color: '#888' }}>, {audiences[activeTab].subtext}.</span>
              </p>
            </div>

            <button onClick={() => setCurrentStep('business')} style={{ display: 'inline-block', backgroundColor: colors.orange, color: '#000', padding: '18px 40px', fontSize: '1.15rem', fontWeight: 600, borderRadius: '8px', border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(232,121,43,0.3)', fontFamily: 'inherit' }}>
              Start Free Assessment →
            </button>
            <p style={{ fontSize: '1rem', color: '#555', marginTop: '16px' }}>
              Free score preview • Full report: <span style={{ color: colors.orange }}>$29.99</span>
            </p>
          </section>

          {/* Problem */}
          <section style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
              The problem with "experts"
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
              {[
                '💰 Consultants get paid to find problems',
                '📋 Insurance agents sell, not advise',
                '🔧 IT companies upsell services'
              ].map((item, i) => (
                <div key={i} style={{ backgroundColor: '#141414', borderRadius: '8px', padding: '16px 20px', fontSize: '1.1rem', color: '#999', border: '1px solid #1a1a1a', textAlign: 'center' }}>
                  {item}
                </div>
              ))}
            </div>

            <p style={{ fontSize: '1.2rem', color: '#fff' }}>
              What if you knew <span style={{ color: colors.orange }}>where you stand</span> first?
            </p>
          </section>

          {/* Credibility */}
          <section style={{ padding: '48px 24px', backgroundColor: '#0f0f0f' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
                Built on real standards
              </h2>
              
              <div style={{ backgroundColor: '#141414', borderRadius: '12px', padding: '24px', border: '1px solid #252525', marginBottom: '16px' }}>
                <p style={{ fontSize: '1.05rem', color: '#ccc', lineHeight: 1.7, margin: 0 }}>
                  Built on the <span style={{ color: colors.orange, fontWeight: 600 }}>NIST Cybersecurity Framework</span> and <span style={{ color: colors.orange, fontWeight: 600 }}>CIS Controls</span>. The same standards used by government agencies and Fortune 500 companies.
                </p>
              </div>
              
              <div style={{ backgroundColor: '#141414', borderRadius: '12px', padding: '24px', border: '1px solid #252525' }}>
                <p style={{ fontSize: '1.05rem', color: '#ccc', lineHeight: 1.7, margin: 0 }}>
                  Created by a <span style={{ color: '#fff', fontWeight: 500 }}>20-year IT operations professional</span> who's worked with organizations of every size. Enterprise-grade frameworks, delivered in a practical, education-first way.
                </p>
              </div>
            </div>
          </section>

          {/* Features */}
          <section style={{ padding: '48px 24px' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '28px' }}>
                Your report includes
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {[
                  { icon: '🎯', title: 'Find Gaps', items: 'Passwords, backups, data, vendors' },
                  { icon: '📊', title: 'Understand Risk', items: 'Plain English, real examples' },
                  { icon: '✅', title: 'Action Plan', items: '30-day roadmap, time estimates' },
                  { icon: '🔗', title: 'Resources', items: 'Free government guides included' }
                ].map((card, i) => (
                  <div key={i} style={{ backgroundColor: '#1a1a1a', borderRadius: '10px', padding: '20px', border: '1px solid #252525', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', marginBottom: '10px' }}>{card.icon}</div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{card.title}</h3>
                    <p style={{ fontSize: '0.95rem', color: '#666', margin: 0, lineHeight: 1.5 }}>{card.items}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Sample Report */}
          <section style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
              Sample report preview
            </h2>

            <div style={{ backgroundColor: '#141414', borderRadius: '12px', padding: '24px', border: '1px solid #252525' }}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: colors.orange }}>
                  312<span style={{ fontSize: '1rem', color: '#666' }}>/500</span>
                </div>
                <span style={{ backgroundColor: 'rgba(232,121,43,0.2)', color: colors.orange, padding: '4px 12px', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 600 }}>ELEVATED RISK</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '20px', fontSize: '1rem' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e74c3c' }}>4</div>
                  <div style={{ color: '#666', fontSize: '0.85rem' }}>Critical</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f39c12' }}>6</div>
                  <div style={{ color: '#666', fontSize: '0.85rem' }}>Attention</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#27ae60' }}>15</div>
                  <div style={{ color: '#666', fontSize: '0.85rem' }}>Good</div>
                </div>
              </div>

              <div style={{ backgroundColor: '#1a1a1a', borderRadius: '8px', padding: '16px', marginBottom: '16px', textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#e74c3c', fontWeight: 600, fontSize: '1rem' }}>🔴 MFA Not Enabled</span>
                  <span style={{ backgroundColor: '#e74c3c', color: '#fff', padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>CRITICAL</span>
                </div>
                <p style={{ color: '#888', fontSize: '0.95rem', margin: 0 }}>
                  Fix: Enable on email, banking, cloud. <span style={{ color: colors.orange }}>⏱️ 30-60 min</span>
                </p>
              </div>

              <div style={{ backgroundColor: '#1a1a1a', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <p style={{ color: '#fff', fontWeight: 600, fontSize: '1rem', margin: '0 0 12px' }}>🎯 30-Day Action Plan</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {['Enable MFA', 'Test backups', 'Staff accounts'].map((item, i) => (
                    <span key={i} style={{ backgroundColor: '#252525', color: '#888', padding: '6px 12px', borderRadius: '6px', fontSize: '0.9rem' }}>{item}</span>
                  ))}
                  <span style={{ color: colors.orange, fontSize: '0.9rem', padding: '6px' }}>+12 more</span>
                </div>
              </div>
            </div>

            <a href="/sample-report.html" target="_blank" style={{ display: 'inline-block', color: colors.orange, marginTop: '20px', fontSize: '1rem', textDecoration: 'none' }}>
              View full sample →
            </a>
          </section>

          {/* Price Comparison */}
          <section style={{ padding: '48px 24px', backgroundColor: '#0f0f0f' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '28px' }}>
                The math
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div style={{ backgroundColor: '#1a1a1a', borderRadius: '10px', padding: '20px 12px', border: '1px solid #252525' }}>
                  <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 8px' }}>Consultant</p>
                  <p style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px' }}>$5,000+</p>
                  <p style={{ color: '#444', fontSize: '0.85rem', margin: 0 }}>4-6 weeks</p>
                </div>
                <div style={{ backgroundColor: '#1a1a1a', borderRadius: '10px', padding: '20px 12px', border: '1px solid #252525' }}>
                  <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 8px' }}>Ignore it</p>
                  <p style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px' }}>$0</p>
                  <p style={{ color: '#444', fontSize: '0.85rem', margin: 0 }}>Until breach</p>
                </div>
                <div style={{ backgroundColor: 'rgba(232,121,43,0.1)', borderRadius: '10px', padding: '20px 12px', border: `2px solid ${colors.orange}`, position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', backgroundColor: colors.orange, color: '#000', padding: '3px 10px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600 }}>SMART</div>
                  <p style={{ color: colors.orange, fontSize: '0.9rem', margin: '0 0 8px' }}>BreachBlockIQ</p>
                  <p style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px' }}>$29.99</p>
                  <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>Under 10 min</p>
                </div>
              </div>
            </div>
          </section>

          {/* What It Is */}
          <section style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
              What this is
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', fontSize: '1rem' }}>
              <div>
                <p style={{ color: '#27ae60', fontWeight: 600, marginBottom: '12px', fontSize: '1.1rem' }}>✓ IS</p>
                {['Clarity before spending', 'Prioritized roadmap', 'Smart first step'].map((item, i) => (
                  <p key={i} style={{ color: '#888', margin: '8px 0' }}>{item}</p>
                ))}
              </div>
              <div>
                <p style={{ color: '#e74c3c', fontWeight: 600, marginBottom: '12px', fontSize: '1.1rem' }}>✗ IS NOT</p>
                {['Compliance cert', 'Expert replacement', 'Legal audit'].map((item, i) => (
                  <p key={i} style={{ color: '#555', margin: '8px 0' }}>{item}</p>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section style={{ padding: '48px 24px', backgroundColor: '#0f0f0f' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
                FAQ
              </h2>

              {[
                { q: "I'm not technical. Will I understand this?", a: "If you can answer yes, no, or not sure, you can do this. Plain English questions, clear next steps in the report." },
                { q: "Isn't this just a free checklist?", a: "Free checklists tell you WHAT to check. Our AI report tells you WHERE you're vulnerable and HOW to prioritize based on YOUR answers. That's a consultant's job." },
                { q: "What if I don't have time right now?", a: "Under 10 minutes. Start today, finish tomorrow. Your access never expires. Report generates instantly when done." },
                { q: "Is my business too small for this?", a: "If you have customer data, employee records, or a bank account, you have risk. Small businesses are the #1 target precisely BECAUSE they think they're too small to matter." },
                { q: "How is this different from hiring a consultant?", a: "Consultants charge $5,000+ and take weeks. This gives you 80% of that value in under 10 minutes for $29.99. For most small businesses, that's enough to find and prioritize your biggest gaps." }
              ].map((faq, i) => (
                <div key={i} style={{ borderBottom: '1px solid #252525', padding: '20px 0', textAlign: 'center' }}>
                  <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>{faq.q}</h3>
                  <p style={{ color: '#666', fontSize: '1rem', margin: 0, lineHeight: 1.6 }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section style={{ padding: '60px 24px', textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 600, color: '#fff', marginBottom: '32px', lineHeight: 1.4 }}>
              Get a clear picture of your security posture before small issues become big problems.
            </h2>

            <button onClick={() => setCurrentStep('business')} style={{ display: 'inline-block', backgroundColor: colors.orange, color: '#000', padding: '18px 40px', fontSize: '1.15rem', fontWeight: 600, borderRadius: '8px', border: 'none', cursor: 'pointer', boxShadow: '0 4px 24px rgba(232,121,43,0.35)', fontFamily: 'inherit' }}>
              Start Free Assessment →
            </button>
            
            <p style={{ fontSize: '1rem', color: '#555', marginTop: '16px' }}>
              Free preview • Full report: <span style={{ color: colors.orange, fontWeight: 600 }}>$29.99</span> • ~10 min
            </p>
          </section>

          {/* Disclaimer */}
          <div style={{ padding: '20px 24px', backgroundColor: 'rgba(232,121,43,0.05)', borderTop: '1px solid rgba(232,121,43,0.1)', textAlign: 'center' }}>
            <p style={{ maxWidth: '600px', margin: '0 auto', fontSize: '0.9rem', color: '#555' }}>
              <span style={{ color: colors.orange }}>📋</span> Educational guidance only. Not a compliance certification or legal advice.
            </p>
          </div>

          {/* Footer */}
          <footer style={{ padding: '32px 24px', textAlign: 'center', borderTop: '1px solid #1a1a1a' }}>
            <p style={{ fontSize: '1rem', marginBottom: '8px' }}>
              <span style={{ color: '#fff' }}>TechShield</span> <span style={{ color: colors.orange }}>KC LLC</span>
            </p>
            <p style={{ fontSize: '0.9rem', color: '#444' }}>
              <a href="/privacy.html" style={{ color: '#444', marginRight: '16px', textDecoration: 'none' }}>Privacy</a>
              <a href="/terms.html" style={{ color: '#444', textDecoration: 'none' }}>Terms</a>
            </p>
            <p style={{ fontSize: '0.85rem', color: '#333', marginTop: '12px' }}>© 2026 BreachBlockIQ™</p>
          </footer>

        </div>
      </div>
    );
  }

  // Business Info Screen
  if (currentStep === 'business') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0a0a0a', color: '#e5e5e5', fontFamily: "'Inter', system-ui, sans-serif", lineHeight: 1.6 }}>
        <style>{globalStyles}</style>
        
        <div style={{ maxWidth: '500px', margin: '0 auto', padding: '24px' }}>
          
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
            <button
              onClick={() => setCurrentStep('intro')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: '1rem',
                fontFamily: 'inherit',
                padding: 0
              }}
            >
              ← Back
            </button>
            <span style={{ color: '#555', fontSize: '0.9rem' }}>⏱️ Under 10 minutes</span>
          </div>

          {/* Title */}
          <h1 style={{ fontSize: '1.75rem', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>
            Personalize your report
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#888', marginBottom: '24px' }}>
            We'll customize your report with <span style={{ color: '#fff' }}>industry specific threats</span> and benchmarks.
          </p>

          {/* Score explanation */}
          <div style={{ backgroundColor: '#141414', border: '1px solid #252525', borderRadius: '8px', padding: '16px', marginBottom: '32px' }}>
            <p style={{ fontSize: '0.95rem', color: '#999', margin: 0, lineHeight: 1.6 }}>
              <span style={{ color: colors.orange, fontWeight: 600 }}>BreachBlockIQ™ Score:</span> 25 questions × 20 points each = <span style={{ color: '#fff', fontWeight: 600 }}>500 max</span>. Higher is better. Your score reflects security readiness based on industry standards.
            </p>
          </div>

          {/* Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Org Name */}
            <div>
              <label style={{ display: 'block', fontSize: '1rem', fontWeight: 500, color: '#fff', marginBottom: '8px' }}>
                Organization Name <span style={{ color: colors.orange }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Your Business Name"
                value={businessInfo.name}
                onChange={e => setBusinessInfo(prev => ({ ...prev, name: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '1rem',
                  backgroundColor: '#141414',
                  border: '1px solid #252525',
                  borderRadius: '8px',
                  color: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={e => e.target.style.borderColor = colors.orange}
                onBlur={e => e.target.style.borderColor = '#252525'}
              />
            </div>

            {/* Industry */}
            <div>
              <label style={{ display: 'block', fontSize: '1rem', fontWeight: 500, color: '#fff', marginBottom: '8px' }}>
                Industry <span style={{ color: colors.orange }}>*</span>
              </label>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px', marginTop: 0 }}>
                This customizes your report with relevant threats
              </p>
              <select
                value={businessInfo.industry}
                onChange={e => setBusinessInfo(prev => ({ ...prev, industry: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '1rem',
                  backgroundColor: '#141414',
                  border: '1px solid #252525',
                  borderRadius: '8px',
                  color: businessInfo.industry ? '#fff' : '#666',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Select your industry...</option>
                <option value="Healthcare / Medical">Healthcare / Medical</option>
                <option value="Financial Services">Financial Services</option>
                <option value="Professional Services">Professional Services (Legal, Accounting, Consulting)</option>
                <option value="Religious Organization">Religious Organization (Church, Synagogue, Mosque, Temple)</option>
                <option value="Education">Education (Public School, District, University)</option>
                <option value="Private School">Private School / Academy</option>
                <option value="Nonprofit / Association">Nonprofit / Association</option>
                <option value="Retail / E-commerce">Retail / E-commerce</option>
                <option value="Construction">Construction (GCs, Builders, Developers)</option>
                <option value="Manufacturing / Warehouse">Manufacturing / Warehouse</option>
                <option value="Restaurant / Hospitality">Restaurant / Hospitality</option>
                <option value="Real Estate">Real Estate</option>
                <option value="IT / Technology Services">IT / Technology Services (Managed Services, Computer Repair, Web/Software)</option>
                <option value="Daycare / Childcare">Daycare / Childcare Center</option>
                <option value="Funeral Services">Funeral Services (Funeral Home, Crematory, Cemetery)</option>
                <option value="Personal Services">Personal Services (Salon, Spa, Fitness, Pet Care)</option>
                <option value="Trades & Repair Services">Trades & Repair Services (HVAC, Plumbing, Auto, Electrical)</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Org Size */}
            <div>
              <label style={{ display: 'block', fontSize: '1rem', fontWeight: 500, color: '#fff', marginBottom: '8px' }}>
                Organization Size <span style={{ color: colors.orange }}>*</span>
              </label>
              <select
                value={businessInfo.size}
                onChange={e => setBusinessInfo(prev => ({ ...prev, size: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '1rem',
                  backgroundColor: '#141414',
                  border: '1px solid #252525',
                  borderRadius: '8px',
                  color: businessInfo.size ? '#fff' : '#666',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">Select size...</option>
                <option value="1 to 5 employees">1 to 5 employees</option>
                <option value="6 to 20 employees">6 to 20 employees</option>
                <option value="21 to 50 employees">21 to 50 employees</option>
                <option value="51 to 100 employees">51 to 100 employees</option>
                <option value="100+ employees">100+ employees</option>
              </select>
            </div>

          </div>

          {/* CTA Button */}
          <button
            onClick={() => setCurrentStep('questions')}
            disabled={!businessInfo.name.trim() || !businessInfo.industry || !businessInfo.size}
            style={{
              width: '100%',
              marginTop: '40px',
              padding: '18px',
              fontSize: '1.1rem',
              fontWeight: 600,
              backgroundColor: (businessInfo.name.trim() && businessInfo.industry && businessInfo.size) ? colors.orange : '#1a1a1a',
              color: (businessInfo.name.trim() && businessInfo.industry && businessInfo.size) ? '#000' : '#555',
              border: 'none',
              borderRadius: '8px',
              cursor: (businessInfo.name.trim() && businessInfo.industry && businessInfo.size) ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              boxShadow: (businessInfo.name.trim() && businessInfo.industry && businessInfo.size) ? '0 4px 20px rgba(232,121,43,0.3)' : 'none'
            }}
          >
            Start My Assessment →
          </button>

          {/* Validation message */}
          {(!businessInfo.name.trim() || !businessInfo.industry || !businessInfo.size) && (
            <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#555', marginTop: '12px' }}>
              Please complete all fields to continue
            </p>
          )}

          {/* Trust Signal */}
          <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#555', marginTop: '20px' }}>
            🔒 Your info stays private. We never sell data.
          </p>

          {/* Help */}
          <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#444', marginTop: '32px' }}>
            Questions? <a href="mailto:info@techshieldkc.com" style={{ color: colors.orange, textDecoration: 'none' }}>info@techshieldkc.com</a>
          </p>

        </div>
      </div>
    );
  }

  // Questions Screen
  if (currentStep === 'questions') {
    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ maxWidth: '650px', margin: '0 auto' }}>
          {/* Progress */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ color: colors.gray, fontSize: '0.85rem' }}>
                {currentCategoryData.icon} {currentCategoryData.title}
              </span>
              <span style={{ color: colors.gray, fontSize: '0.85rem' }}>
                {answeredQuestions}/{totalQuestions}
              </span>
            </div>
            <div style={{
              height: '3px',
              background: colors.darkCard,
              borderRadius: '100px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${(answeredQuestions / totalQuestions) * 100}%`,
                background: colors.orange,
                borderRadius: '100px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>

          {/* Reassurance Message */}
          <div style={{
            background: colors.darkCard,
            border: `1px solid ${colors.grayDark}22`,
            borderRadius: '8px',
            padding: '0.875rem 1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>💡</span>
            <p style={{ 
              fontSize: '0.85rem', 
              color: colors.gray, 
              lineHeight: 1.5,
              margin: 0
            }}>
              <strong style={{ color: colors.grayLight }}>Answer as best you can.</strong> All questions help us give you better guidance, but it's okay if you're unsure about some answers-just select "Not sure" and we'll flag it as an area to investigate. No pressure, no judgment.
            </p>
          </div>
          
          {/* Category Tabs */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            overflowX: 'auto',
            paddingBottom: '0.5rem'
          }}>
            {categories.map((cat, i) => {
              const catAnswered = cat.questions.filter(q => answers[q.id]).length;
              const catComplete = catAnswered === cat.questions.length;
              return (
                <button
                  key={cat.id}
                  onClick={() => setCurrentCategory(i)}
                  style={{
                    background: currentCategory === i ? colors.orangeLight : colors.darkCard,
                    border: `1px solid ${currentCategory === i ? colors.orangeBorder : 'transparent'}`,
                    borderRadius: '100px',
                    padding: '0.4rem 0.9rem',
                    color: currentCategory === i ? colors.orange : colors.gray,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  {cat.icon} {cat.title}
                  {catComplete && <span style={{ color: '#22c55e' }}>✓</span>}
                </button>
              );
            })}
          </div>
          
          {/* Questions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {currentCategoryData.questions.map((question, qIndex) => (
              <div
                key={question.id}
                style={{
                  background: colors.darkCard,
                  border: `1px solid ${colors.grayDark}22`,
                  borderRadius: '12px',
                  padding: '1.25rem'
                }}
              >
                <p style={{ 
                  marginBottom: '0.5rem', 
                  fontWeight: 500,
                  lineHeight: 1.5,
                  fontSize: '0.95rem'
                }}>
                  {qIndex + 1}. {question.text}
                </p>
                
                {/* Helper Text */}
                {question.helper && (
                  <p style={{
                    fontSize: '0.8rem',
                    color: colors.gray,
                    marginBottom: '1rem',
                    lineHeight: 1.5,
                    paddingLeft: '0.75rem',
                    borderLeft: `2px solid ${colors.grayDark}44`
                  }}>
                    💡 {question.helper}
                  </p>
                )}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {question.options.map(option => (
                    <button
                      key={option}
                      onClick={() => handleAnswer(question.id, option)}
                      style={{
                        background: answers[question.id] === option ? colors.orangeLight : colors.darkBg,
                        border: `1px solid ${answers[question.id] === option ? colors.orangeBorder : colors.grayDark}33`,
                        borderRadius: '8px',
                        padding: '0.7rem 1rem',
                        color: answers[question.id] === option ? colors.orange : colors.grayLight,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: '0.9rem',
                        textAlign: 'left',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Navigation */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: '1.5rem',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                if (currentCategory > 0) {
                  setCurrentCategory(currentCategory - 1);
                } else {
                  setCurrentStep('business');
                }
              }}
              style={{
                background: colors.darkCard,
                border: `1px solid ${colors.grayDark}33`,
                borderRadius: '8px',
                padding: '0.875rem 1.25rem',
                minWidth: '100px',
                flex: '1 1 auto',
                color: colors.gray,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '0.95rem'
              }}
            >
              ← Back
            </button>
            
            {currentCategory < categories.length - 1 ? (
              <button
                onClick={() => setCurrentCategory(currentCategory + 1)}
                disabled={!allQuestionsAnswered}
                style={{
                  background: allQuestionsAnswered ? colors.orange : colors.darkCard,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.875rem 1.25rem',
                  color: allQuestionsAnswered ? colors.white : colors.grayDark,
                  cursor: allQuestionsAnswered ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={() => setCurrentStep('preview')}
                disabled={answeredQuestions < totalQuestions}
                style={{
                  background: answeredQuestions >= totalQuestions ? '#22c55e' : colors.darkCard,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.875rem 1.25rem',
                  color: answeredQuestions >= totalQuestions ? colors.white : colors.grayDark,
                  cursor: answeredQuestions >= totalQuestions ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}
              >
                See My Results →
              </button>
            )}
          </div>
        </div>

        {/* Paywall Modal */}
        {showPaywall && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000,
            overflowY: 'auto'
          }}>
            <div style={{
              background: colors.black,
              border: `1px solid ${colors.grayDark}44`,
              borderRadius: '16px',
              padding: '1.5rem',
              maxWidth: '420px',
              width: '100%',
              position: 'relative',
              margin: '1rem 0'
            }}>
              <button
                onClick={() => setShowPaywall(false)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'transparent',
                  border: 'none',
                  color: colors.gray,
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  lineHeight: 1
                }}
              >
                ×
              </button>

              {/* Compact Score + Plans */}
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem' }}>
                  Your Results Are Ready
                </h3>
                
                {(() => {
                  const { highRisk, mediumRisk, lowRisk, iqScore } = calculateRiskScore();
                  const riskLevel = iqScore < 200 ? 'High Risk' : iqScore < 300 ? 'Elevated Risk' : iqScore < 400 ? 'Moderate' : 'Strong';
                  const riskColor = iqScore < 200 ? '#ef4444' : iqScore < 300 ? '#eab308' : iqScore < 400 ? '#22c55e' : '#22c55e';
                  return (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      background: colors.darkBg,
                      border: `2px solid ${colors.orange}`,
                      borderRadius: '8px',
                      padding: '0.5rem 1rem'
                    }}>
                      <span style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.orange }}>{iqScore}</span>
                      <span style={{ color: colors.gray }}>/500</span>
                      <span style={{ color: riskColor, fontWeight: 600, fontSize: '0.8rem' }}>• {riskLevel}</span>
                      <span style={{ color: colors.gray, fontSize: '0.75rem' }}>({highRisk} critical)</span>
                    </div>
                  );
                })()}
              </div>
              
              {/* What's Included */}
              <div style={{ 
                fontSize: '0.75rem', 
                color: colors.grayLight, 
                marginBottom: '1rem',
                lineHeight: 1.6
              }}>
                {[
                  'BreachBlockIQ™ score with risk level',
                  'All 25 areas analyzed individually',
                  'Your answer + why it matters + what to do',
                  'Industry-specific threat context',
                  '30-day action roadmap'
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: colors.orange }}>✓</span> {item}
                  </div>
                ))}
              </div>

              {/* Plan Selection */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: colors.gray, marginBottom: '0.5rem', textAlign: 'center' }}>
                  Choose your plan:
                </div>
                
                {/* One-Time Option */}
                <div 
                  onClick={() => setSelectedPlan('onetime')}
                  style={{
                    background: selectedPlan === 'onetime' ? colors.orangeLight : colors.darkCard,
                    border: `2px solid ${selectedPlan === 'onetime' ? colors.orange : colors.grayDark}44`,
                    borderRadius: '8px',
                    padding: '0.75rem',
                    marginBottom: '0.4rem',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: `2px solid ${selectedPlan === 'onetime' ? colors.orange : colors.gray}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {selectedPlan === 'onetime' && (
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors.orange }} />
                        )}
                      </div>
                      <div style={{ fontWeight: 600, color: colors.white, fontSize: '0.9rem' }}>Full Report</div>
                    </div>
                    <div style={{ fontWeight: 700, color: colors.orange, fontSize: '1rem' }}>
                      {appliedDiscount > 0 && appliedDiscount < 100 ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: colors.gray, marginRight: '8px' }}>$29.99</span>
                          ${discountedPrice.toFixed(2)}
                        </>
                      ) : '$29.99'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Capture */}
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.35rem', 
                  fontSize: '0.8rem',
                  color: colors.grayLight
                }}>
                  Email address <span style={{ color: colors.orange }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={businessInfo.email || ''}
                  onChange={e => setBusinessInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="you@yourbusiness.com"
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    background: colors.darkCard,
                    border: `1px solid ${colors.grayDark}44`,
                    borderRadius: '6px',
                    color: colors.white,
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = colors.orange}
                  onBlur={e => e.target.style.borderColor = `${colors.grayDark}44`}
                />
              </div>

              {/* Promo Code Section */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoError('');
                    }}
                    placeholder="Promo code (optional)"
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.grayDark}`,
                      background: colors.darkCard,
                      color: colors.white,
                      fontFamily: 'inherit',
                      fontSize: '0.85rem'
                    }}
                  />
                  <button
                    onClick={async () => {
                      if (!businessInfo.email || !businessInfo.email.includes('@')) {
                        setPromoError('Enter email first');
                        return;
                      }
                      if (!promoCode.trim()) {
                        return;
                      }
                      try {
                        const response = await fetch('/api/validate-promo', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ code: promoCode })
                        });
                        const data = await response.json();
                        if (data.valid) {
                          setAppliedDiscount(data.discount);
                          setDiscountedPrice(data.finalPrice);
                          
                          if (data.discount === 100) {
                            setPromoError('');
                            setPaymentComplete(true);
                            setShowPaywall(false);
                            await generateReport();
                          } else {
                            setPromoError(`✓ ${data.discount}% off! Pay $${data.finalPrice.toFixed(2)}`);
                          }
                        } else {
                          setPromoError('Invalid code');
                        }
                      } catch (error) {
                        setPromoError('Error validating code');
                      }
                    }}
                    style={{
                      background: colors.orange,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.6rem 1rem',
                      color: colors.white,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '0.85rem',
                      fontWeight: 600
                    }}
                  >
                    Apply
                  </button>
                </div>
                {promoError && (
                  <p style={{ color: promoError.startsWith('✓') ? '#22c55e' : '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem', fontWeight: promoError.startsWith('✓') ? 600 : 400 }}>{promoError}</p>
                )}
              </div>

              {/* PayPal Button Container */}
              <div 
                ref={paypalRef} 
                style={{ 
                  marginBottom: '0.5rem',
                  minHeight: '150px',
                  opacity: businessInfo.email && businessInfo.email.includes('@') ? 1 : 0.5,
                  pointerEvents: businessInfo.email && businessInfo.email.includes('@') ? 'auto' : 'none',
                  display: paypalLoading ? 'none' : 'block'
                }}
              >
                {/* PayPal button renders here */}
              </div>
              
              {paypalLoading && businessInfo.email && businessInfo.email.includes('@') && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem',
                  minHeight: '150px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '30px',
                    height: '30px',
                    border: `3px solid ${colors.darkCard}`,
                    borderTop: `3px solid ${colors.orange}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '0.75rem'
                  }} />
                  <p style={{ fontSize: '0.85rem', color: colors.gray }}>Loading payment options...</p>
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </div>
              )}

              {!businessInfo.email || !businessInfo.email.includes('@') ? (
                <p style={{ 
                  textAlign: 'center', 
                  fontSize: '0.75rem', 
                  color: colors.orange,
                  marginBottom: '0.35rem'
                }}>
                  ↑ Enter email to continue
                </p>
              ) : null}
              
              <p style={{ 
                textAlign: 'center', 
                fontSize: '0.7rem', 
                color: colors.grayDark 
              }}>
                Secure payment via PayPal • <a href="mailto:info@techshieldkc.com" style={{ color: colors.gray }}>Questions?</a>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Loading Screen - only show when generating report, not when report is ready
  if (loading && currentStep !== 'report') {
    return (
      <div style={{
        ...baseStyles,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        minHeight: '100vh'
      }}>
        <style>{`
          ${globalStyles}
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes dots {
            0%, 20% { content: '.'; }
            40% { content: '..'; }
            60%, 100% { content: '...'; }
          }
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }
        `}</style>
        
        {/* Spinner */}
        <div style={{
          width: '70px',
          height: '70px',
          border: `5px solid ${colors.darkCard}`,
          borderTopColor: colors.orange,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '2rem'
        }} />
        
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', color: colors.white }}>
          Building Your Report
        </h2>
        
        {/* Animated dots */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
          <div style={{
            width: '12px',
            height: '12px',
            background: colors.orange,
            borderRadius: '50%',
            animation: 'bounce 1.4s infinite ease-in-out',
            animationDelay: '0s'
          }} />
          <div style={{
            width: '12px',
            height: '12px',
            background: colors.orange,
            borderRadius: '50%',
            animation: 'bounce 1.4s infinite ease-in-out',
            animationDelay: '0.2s'
          }} />
          <div style={{
            width: '12px',
            height: '12px',
            background: colors.orange,
            borderRadius: '50%',
            animation: 'bounce 1.4s infinite ease-in-out',
            animationDelay: '0.4s'
          }} />
        </div>
        
        <p style={{ color: colors.grayLight, fontSize: '1rem', marginBottom: '0.5rem' }}>
          Analyzing your 25 responses
        </p>
        <p style={{ color: colors.gray, fontSize: '0.9rem' }}>
          Please wait 15-20 seconds while we generate your personalized report
        </p>
        
        {/* Progress hint */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem 1.5rem',
          background: colors.darkCard,
          borderRadius: '10px',
          maxWidth: '320px',
          textAlign: 'center'
        }}>
          <p style={{ color: colors.gray, fontSize: '0.85rem', margin: 0 }}>
            📄 Report length depends on issues found
          </p>
        </div>
      </div>
    );
  }

  // Preview Screen - INSTANT teaser (uses background report if ready, otherwise estimates)
  if (currentStep === 'preview') {
    // Calculate rough estimates from their answers
    const calculateEstimates = () => {
      let critical = 0;
      let attention = 0;
      let good = 0;
      
      Object.values(answers).forEach(answer => {
        const lower = answer.toLowerCase();
        if (lower.includes('no') || lower.includes('never') || lower.includes('not sure') || lower.includes('none')) {
          critical++;
        } else if (lower.includes('some') || lower.includes('partial') || lower.includes('occasionally') || lower.includes('informal')) {
          attention++;
        } else {
          good++;
        }
      });
      
      return { critical, attention, good };
    };
    
    // Use pending report if ready, otherwise use estimates
    const estimates = calculateEstimates();
    const displayData = pendingReport ? {
      critical: pendingReport.criticalCount || 0,
      attention: pendingReport.attentionCount || 0,
      good: pendingReport.goodCount || 0
    } : estimates;

    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem 1rem' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.white, marginBottom: '0.5rem' }}>
              Assessment Complete!
            </h1>
            <p style={{ color: colors.gray, fontSize: '0.9rem' }}>{businessInfo.name}</p>
          </div>

          {/* Locked Score Card */}
          <div style={{
            background: colors.darkCard,
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center',
            marginBottom: '1.5rem',
            border: `2px solid ${colors.orange}`,
            position: 'relative'
          }}>
            <div style={{ fontSize: '4rem', fontWeight: 700, color: colors.orange, filter: 'blur(8px)' }}>
              {pendingReport?.score || 247}
            </div>
            <div style={{ color: colors.gray, fontSize: '1rem', marginBottom: '1rem' }}>out of 500</div>
            <div style={{
              display: 'inline-block',
              background: '#f59e0b',
              color: 'white',
              padding: '0.5rem 1.5rem',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 600,
              filter: 'blur(4px)'
            }}>
              {pendingReport?.riskLevel || 'CALCULATING'}
            </div>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(0,0,0,0.8)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: `1px solid ${colors.orange}`
            }}>
              <span style={{ fontSize: '1.5rem' }}>🔒</span>
              <span style={{ color: colors.white, marginLeft: '0.5rem', fontWeight: 600 }}>Unlock Your Score</span>
            </div>
          </div>

          {/* Stats - REAL numbers, no blur */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              background: '#fee2e2',
              padding: '1rem 1.5rem',
              borderRadius: '10px',
              textAlign: 'center',
              minWidth: '100px'
            }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#dc2626' }}>{displayData.critical}</div>
              <div style={{ fontSize: '0.75rem', color: '#991b1b' }}>Critical</div>
            </div>
            <div style={{
              background: '#fef3c7',
              padding: '1rem 1.5rem',
              borderRadius: '10px',
              textAlign: 'center',
              minWidth: '100px'
            }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#f59e0b' }}>{displayData.attention}</div>
              <div style={{ fontSize: '0.75rem', color: '#92400e' }}>Attention</div>
            </div>
            <div style={{
              background: '#d1fae5',
              padding: '1rem 1.5rem',
              borderRadius: '10px',
              textAlign: 'center',
              minWidth: '100px'
            }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#10b981' }}>{displayData.good}</div>
              <div style={{ fontSize: '0.75rem', color: '#065f46' }}>Good</div>
            </div>
          </div>

          {/* Sample Report Preview - BLURRED */}
          <div style={{
            background: colors.darkCard,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ color: colors.white, fontSize: '1rem', marginBottom: '1rem' }}>
              📋 Sample from your report:
            </h3>
            <div style={{ position: 'relative' }}>
              <div style={{ filter: 'blur(3px)', pointerEvents: 'none' }}>
                <div style={{ background: colors.darkBg, padding: '0.75rem', borderRadius: '8px', marginBottom: '0.5rem', borderLeft: '3px solid #dc2626' }}>
                  <span style={{ color: '#dc2626', fontWeight: 600 }}>Critical:</span>
                  <span style={{ color: colors.gray }}> Your payment verification procedures need immediate attention to prevent wire fraud...</span>
                </div>
                <div style={{ background: colors.darkBg, padding: '0.75rem', borderRadius: '8px', marginBottom: '0.5rem', borderLeft: '3px solid #f59e0b' }}>
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>Attention:</span>
                  <span style={{ color: colors.gray }}> Security awareness training should be formalized with quarterly sessions...</span>
                </div>
                <div style={{ background: colors.darkBg, padding: '0.75rem', borderRadius: '8px', borderLeft: '3px solid #10b981' }}>
                  <span style={{ color: '#10b981', fontWeight: 600 }}>Strong:</span>
                  <span style={{ color: colors.gray }}> Multi-factor authentication is properly configured on critical accounts...</span>
                </div>
              </div>
            </div>
          </div>

          {/* What's in the Report */}
          <div style={{
            background: colors.orangeLight,
            border: `1px solid ${colors.orangeBorder}`,
            borderRadius: '12px',
            padding: '1.25rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <p style={{ fontWeight: 600, color: colors.white, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              Your personalized report includes:
            </p>
            <div style={{ fontSize: '0.9rem', color: colors.grayLight, lineHeight: 1.9 }}>
              <div><span style={{ color: colors.orange }}>✓</span> Your exact BreachBlockIQ™ score</div>
              <div><span style={{ color: colors.orange }}>✓</span> Detailed analysis of all 25 security areas</div>
              <div><span style={{ color: colors.orange }}>✓</span> Specific risks for {businessInfo.industry || 'your'} organizations</div>
              <div><span style={{ color: colors.orange }}>✓</span> Step-by-step fix instructions with time estimates</div>
              <div><span style={{ color: colors.orange }}>✓</span> 30-day action plan prioritized by impact</div>
              <div><span style={{ color: colors.orange }}>✓</span> PDF download for your records</div>
            </div>
          </div>

          {/* Unlock Button */}
          <button
            onClick={() => setShowPaywall(true)}
            style={{
              width: '100%',
              background: colors.orange,
              border: 'none',
              borderRadius: '10px',
              padding: '1rem',
              color: colors.white,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: `0 4px 20px ${colors.orange}40`,
              marginBottom: '1rem'
            }}
          >
            🔓 Unlock Full Report $29.99
          </button>

          <p style={{ textAlign: 'center', color: colors.grayDark, fontSize: '0.75rem' }}>
            One-time purchase • Instant access • PDF download included
          </p>
          
          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: `1px solid ${colors.grayDark}33` }}>
            <p style={{ fontSize: '0.7rem', color: colors.grayDark }}>
              <a href="/privacy.html" style={{ color: colors.grayDark, textDecoration: 'none', marginRight: '1rem' }}>Privacy Policy</a>
              <a href="/terms.html" style={{ color: colors.grayDark, textDecoration: 'none' }}>Terms of Service</a>
            </p>
          </div>
        </div>

        {/* Paywall Modal */}
        {showPaywall && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '0.5rem',
            overflowY: 'auto'
          }}>
            <div style={{
              background: colors.black,
              borderRadius: '16px',
              padding: 'clamp(1rem, 4vw, 2rem)',
              maxWidth: '450px',
              width: '100%',
              maxHeight: '95vh',
              overflowY: 'auto',
              border: `1px solid ${colors.grayDark}33`,
              margin: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.white }}>Unlock Your Report</h2>
                <button
                  onClick={() => setShowPaywall(false)}
                  style={{ background: 'none', border: 'none', color: colors.gray, cursor: 'pointer', fontSize: '1.5rem' }}
                >×</button>
              </div>

              {/* Summary reminder */}
              <div style={{
                background: colors.darkCard,
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ color: colors.gray, fontSize: '0.85rem', textAlign: 'center', marginBottom: '0.5rem' }}>
                  We found issues in your assessment:
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{displayData.critical}</span>
                    <span style={{ fontSize: '0.7rem', color: colors.gray, display: 'block' }}>Critical</span>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f59e0b' }}>{displayData.attention}</span>
                    <span style={{ fontSize: '0.7rem', color: colors.gray, display: 'block' }}>Attention</span>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{displayData.good}</span>
                    <span style={{ fontSize: '0.7rem', color: colors.gray, display: 'block' }}>Good</span>
                  </div>
                </div>
              </div>

              {/* Email field */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', color: colors.gray, fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                  Email (for receipt) <span style={{ color: colors.orange }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={businessInfo.email || ''}
                  onChange={(e) => setBusinessInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.grayDark}`,
                    background: colors.darkCard,
                    color: colors.white,
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Plan options */}
              <div style={{ marginBottom: '1.5rem' }}>
                {/* One-Time Option */}
                <div
                  onClick={() => setSelectedPlan('onetime')}
                  style={{
                    background: selectedPlan === 'onetime' ? colors.orangeLight : colors.darkCard,
                    border: `2px solid ${selectedPlan === 'onetime' ? colors.orange : colors.grayDark}`,
                    borderRadius: '10px',
                    padding: '1rem',
                    cursor: 'pointer',
                    marginBottom: '0.75rem'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        border: `2px solid ${selectedPlan === 'onetime' ? colors.orange : colors.grayDark}`,
                        background: selectedPlan === 'onetime' ? colors.orange : 'transparent'
                      }} />
                      <div style={{ fontWeight: 600, color: colors.white, fontSize: '0.9rem' }}>Full Report</div>
                    </div>
                    <div style={{ fontWeight: 700, color: colors.orange, fontSize: '1.1rem' }}>
                      {appliedDiscount > 0 && appliedDiscount < 100 ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: colors.gray, marginRight: '8px', fontSize: '0.9rem' }}>$29.99</span>
                          ${discountedPrice.toFixed(2)}
                        </>
                      ) : '$29.99'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Promo Code Section */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoError('');
                    }}
                    placeholder="Promo code (optional)"
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.grayDark}`,
                      background: colors.darkCard,
                      color: colors.white,
                      fontFamily: 'inherit',
                      fontSize: '0.85rem'
                    }}
                  />
                  <button
                    onClick={async () => {
                      if (!businessInfo.email || !businessInfo.email.includes('@')) {
                        setPromoError('Enter email first');
                        return;
                      }
                      if (!promoCode.trim()) {
                        return;
                      }
                      try {
                        const response = await fetch('/api/validate-promo', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ code: promoCode })
                        });
                        const data = await response.json();
                        if (data.valid) {
                          setAppliedDiscount(data.discount);
                          setDiscountedPrice(data.finalPrice);
                          
                          if (data.discount === 100) {
                            setPromoError('');
                            setPaymentComplete(true);
                            setShowPaywall(false);
                            await generateReport();
                          } else {
                            setPromoError(`✓ ${data.discount}% off! Pay $${data.finalPrice.toFixed(2)}`);
                          }
                        } else {
                          setPromoError('Invalid code');
                        }
                      } catch (error) {
                        setPromoError('Error validating code');
                      }
                    }}
                    style={{
                      background: colors.orange,
                      border: 'none',
                      borderRadius: '6px',
                      padding: '0.6rem 1rem',
                      color: colors.white,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '0.85rem',
                      fontWeight: 600
                    }}
                  >
                    Apply
                  </button>
                </div>
                {promoError && (
                  <p style={{ color: promoError.startsWith('✓') ? '#22c55e' : '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem', fontWeight: promoError.startsWith('✓') ? 600 : 400 }}>{promoError}</p>
                )}
              </div>

              {/* PayPal Button */}
              <div ref={paypalRef} style={{ marginBottom: '1rem', minHeight: '50px' }}></div>

              <p style={{ color: colors.grayDark, fontSize: '0.7rem', textAlign: 'center' }}>
                Secure payment via PayPal. By purchasing, you agree to our <a href="/terms.html" style={{ color: colors.gray }}>Terms</a> and <a href="/privacy.html" style={{ color: colors.gray }}>Privacy Policy</a>.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }


  // Report Screen - Beautiful PDF download
  if (currentStep === 'report') {
    const downloadPdf = () => {
      console.log('Report data:', report);
      
      if (!report || typeof report.score === 'undefined') {
        alert('Report data not available. Please try again.');
        return;
      }

      const r = report;
      const riskColors = {
        'HIGH RISK': '#dc2626',
        'ELEVATED RISK': '#f59e0b',
        'MODERATE': '#3b82f6',
        'STRONG': '#10b981'
      };
      const riskColor = riskColors[r.riskLevel] || '#f59e0b';
      
      // Human-friendly risk level labels
      const riskLabels = {
        'HIGH RISK': 'HIGH RISK — immediate attention recommended',
        'ELEVATED RISK': 'ELEVATED RISK — improvements needed',
        'MODERATE': 'MODERATE — manageable with focused improvements',
        'STRONG': 'STRONG — maintain and monitor'
      };
      const riskLabel = riskLabels[r.riskLevel] || r.riskLevel;

      // Industry-specific fraud stats with sources (3 per industry)
      const fraudStats = {
        'Religious Organization': [
          { stat: 'Wire fraud targeting religious organizations has increased 300% since 2020.', source: 'FBI IC3 Report 2023' },
          { stat: 'Average loss per church wire fraud incident: $84,000.', source: 'Brotherhood Mutual Claims Data' },
          { stat: '73% of churches have no cybersecurity policy in place.', source: 'Christianity Today Survey 2023' }
        ],
        'Daycare / Childcare': [
          { stat: 'Childcare centers experienced a 67% increase in ransomware attacks targeting parent data.', source: 'CISA Education Sector Report 2023' },
          { stat: 'Average cost of a data breach in childcare: $50,000 plus regulatory fines.', source: 'Ponemon Institute 2023' },
          { stat: '82% of childcare centers lack formal incident response plans.', source: 'Child Care Aware Survey 2023' }
        ],
        'Private School': [
          { stat: 'K-12 schools experienced a 44% increase in cyberattacks year-over-year.', source: 'K12 SIX Cybersecurity Report 2023' },
          { stat: 'Average ransomware demand for schools: $250,000.', source: 'Sophos State of Ransomware 2023' },
          { stat: '1 in 3 private schools have experienced a data breach involving student records.', source: 'NAIS Technology Survey 2023' }
        ],
        'Nonprofit / Association': [
          { stat: 'Nonprofits are 3x more likely to be targeted due to limited security budgets.', source: 'NTEN Nonprofit Tech Survey 2023' },
          { stat: 'Average nonprofit breach cost: $65,000 plus donor trust damage.', source: 'IBM Cost of Data Breach 2023' },
          { stat: '59% of nonprofits have no dedicated cybersecurity staff or budget.', source: 'Nonprofit Technology Network 2023' }
        ],
        'Healthcare / Medical': [
          { stat: 'Healthcare breaches cost an average of $10.9 million per incident, the highest of any industry.', source: 'IBM Cost of Data Breach 2023' },
          { stat: 'HIPAA fines can reach $50,000 per record exposed.', source: 'HHS Office for Civil Rights' },
          { stat: '89% of healthcare organizations experienced a data breach in the past 2 years.', source: 'Ponemon Healthcare Cybersecurity Report 2023' }
        ],
        'Financial Services': [
          { stat: 'Financial services face the second-highest breach costs at $5.9 million average.', source: 'IBM Cost of Data Breach 2023' },
          { stat: 'Wire fraud attempts increased 84% targeting financial service clients.', source: 'FinCEN Advisory 2023' },
          { stat: 'Average time to identify a breach in finance: 177 days.', source: 'Verizon DBIR 2023' }
        ],
        'Retail / E-commerce': [
          { stat: 'Retail data breaches expose customer payment data in 41% of incidents.', source: 'Verizon DBIR 2023' },
          { stat: 'Average retail breach cost: $3.2 million.', source: 'IBM Cost of Data Breach 2023' },
          { stat: 'Payment system malware attacks on small retailers increased 60% year-over-year.', source: 'Trustwave Global Security Report 2023' }
        ],
        'Professional Services': [
          { stat: 'Law firms and accountants are targeted in 25% of all business email compromise attacks.', source: 'FBI IC3 Report 2023' },
          { stat: 'Average professional services breach cost: $4.7 million.', source: 'IBM Cost of Data Breach 2023' },
          { stat: 'Client trust wire fraud targeting professional services averages $125,000 per incident.', source: 'ABA Legal Technology Survey 2023' }
        ],
        'IT / Technology Services': [
          { stat: 'IT service providers are targeted to gain access to multiple downstream clients simultaneously.', source: 'CISA Advisory 2023' },
          { stat: 'Supply chain attacks through IT providers increased 78% year-over-year.', source: 'SonicWall Cyber Threat Report 2023' },
          { stat: 'Average breach through an IT service provider affects 2,620 downstream customers.', source: 'ConnectWise State of Industry Report 2023' }
        ],
        'Education': [
          { stat: 'Educational institutions saw a 75% increase in ransomware attacks year-over-year.', source: 'Sophos State of Ransomware Education 2023' },
          { stat: 'Average education sector breach cost: $3.6 million.', source: 'IBM Cost of Data Breach 2023' },
          { stat: '56% of higher education institutions experienced a phishing attack in the past year.', source: 'EDUCAUSE Cybersecurity Report 2023' }
        ],
        'Construction': [
          { stat: 'Construction wire fraud has increased 400% with attackers impersonating subcontractors.', source: 'FBI IC3 Report 2023' },
          { stat: 'Average wire fraud loss in construction: $175,000 per incident.', source: 'Construction Financial Management Association 2023' },
          { stat: '67% of construction firms have experienced a cyberattack in the past 2 years.', source: 'AGC of America Cybersecurity Survey 2023' }
        ],
        'Trades & Repair Services': [
          { stat: 'Small trades businesses lose an average of $108,000 to business email compromise.', source: 'FBI IC3 Report 2023' },
          { stat: 'Invoice fraud targeting service businesses increased 65% year-over-year.', source: 'Association of Certified Fraud Examiners 2023' },
          { stat: '78% of small service businesses have no cyber insurance coverage.', source: 'Hiscox Cyber Readiness Report 2023' }
        ],
        'Personal Services': [
          { stat: 'Salons and spas are targeted for customer credit card and personal data in 34% of small business breaches.', source: 'Verizon DBIR 2023' },
          { stat: 'Average payment system breach cost for personal services: $45,000.', source: 'National Federation of Independent Business 2023' },
          { stat: 'Appointment booking system breaches exposed 2.3 million customer records in 2023.', source: 'Identity Theft Resource Center 2023' }
        ],
        'Funeral Services': [
          { stat: 'Funeral homes are targeted for deceased personal information. Criminals exploit that deceased cannot monitor credit.', source: 'FTC Consumer Sentinel Network 2023' },
          { stat: 'Identity theft using deceased individuals data increased 40% year-over-year.', source: 'Identity Theft Resource Center 2023' },
          { stat: 'Average funeral home breach involving pre-need contracts: $62,000 in liability.', source: 'National Funeral Directors Association 2023' }
        ],
        'Manufacturing / Warehouse': [
          { stat: 'Manufacturing ransomware attacks cause average downtime of 21 days.', source: 'Dragos Industrial Cybersecurity Report 2023' },
          { stat: 'Average manufacturing breach cost: $2.4 million.', source: 'IBM Cost of Data Breach 2023' },
          { stat: 'Industrial control system attacks increased 87% targeting small manufacturers.', source: 'CISA ICS Advisory 2023' }
        ],
        'Restaurant / Hospitality': [
          { stat: 'Hospitality payment system breaches expose customer payment data in 67% of incidents.', source: 'Verizon DBIR 2023' },
          { stat: 'Average restaurant data breach cost: $1.3 million including credit card compliance fines.', source: 'National Restaurant Association 2023' },
          { stat: 'Reservation system breaches increased 55% targeting customer data.', source: 'Hospitality Technology Report 2023' }
        ],
        'Real Estate': [
          { stat: 'Real estate wire fraud costs buyers an average of $150,000 per incident.', source: 'FBI IC3 Report 2023' },
          { stat: 'Wire fraud attempts occur on nearly 1 in 3 real estate transactions.', source: 'American Land Title Association 2023' },
          { stat: 'Email compromise targeting real estate increased 110% year-over-year.', source: 'CertifID Wire Fraud Report 2023' }
        ],
        'Other': [
          { stat: 'Small businesses face 43% of all cyberattacks, yet only 14% are prepared.', source: 'Verizon DBIR 2023' },
          { stat: '60% of small businesses close within 6 months of a cyberattack.', source: 'National Cyber Security Alliance' },
          { stat: 'Average small business breach cost: $200,000.', source: 'IBM Cost of Data Breach 2023' }
        ]
      };
      const industryStats = fraudStats[businessInfo.industry] || fraudStats['Other'];
      const randomStat = industryStats[Math.floor(Math.random() * industryStats.length)];

      // Build priorities HTML
      let prioritiesHtml = '';
      (r.priorities || []).forEach((p, i) => {
        prioritiesHtml += '<div class="priority-box"><h4>' + (i + 1) + '. ' + (p.title || 'Priority') + '</h4><p>' + (p.reason || '') + '</p></div>';
      });
      if (!prioritiesHtml) prioritiesHtml = '<p>No priorities identified.</p>';

      // Build critical issues HTML with enhanced HRShieldIQ format
      let criticalHtml = '';
      if ((r.criticalIssues || []).length > 0) {
        criticalHtml = '<h2>Areas Flagged for Review</h2>';
        r.criticalIssues.forEach((issue, idx) => {
          const isFirst = idx === 0;
          criticalHtml += '<div class="issue-card critical">' +
            '<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:15px;flex-wrap:wrap;gap:10px;">' +
            '<div class="issue-title">' + (issue.topic || 'Issue') + '</div>' +
            '<div style="display:flex;gap:8px;">' + 
            '<span class="badge critical">' + (isFirst ? '🔥 FIX FIRST' : 'CRITICAL') + '</span>' +
            '</div></div>' +
            '<div style="background:#f1f5f9;padding:12px 16px;border-radius:8px;margin:12px 0;border-left:4px solid #94a3b8;">' +
            '<div style="font-family:Inter,sans-serif;font-size:9pt;color:#64748b;text-transform:uppercase;font-weight:600;">Your Answer</div>' +
            '<div style="color:#334155;font-style:italic;margin-top:4px;">"' + (issue.answer || 'See details') + '"</div></div>' +
            '<div class="issue-grid">' +
            '<div class="issue-box risk"><div class="issue-box-label">⚠️ Why It Matters</div><div class="issue-box-text">' + (issue.risk || 'This gap may expose your organization to preventable incidents.') + '</div></div>' +
            '<div class="issue-box effort"><div class="issue-box-label">💰 Potential Impact</div><div class="issue-box-text">' + (issue.impact || 'Varies by incident severity') + '</div></div>' +
            '<div class="issue-box fix"><div class="issue-box-label">✅ Suggested Steps</div><div class="issue-box-text">' + (issue.fix || 'Consider addressing this area') + '</div></div>' +
            '<div class="issue-box" style="background:#f0f9ff;border:1px solid #bae6fd;"><div class="issue-box-label" style="color:#0369a1;">⏱️ Effort</div><div class="issue-box-text"><strong>' + (issue.effort || 'Varies') + '</strong></div></div>' +
            '</div></div>';
        });
      }

      // Build attention issues HTML with HRShieldIQ format
      let attentionHtml = '';
      if ((r.attentionIssues || []).length > 0) {
        attentionHtml = '<h2>Items Worth Reviewing</h2>';
        r.attentionIssues.forEach(issue => {
          attentionHtml += '<div class="issue-card attention">' +
            '<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:15px;flex-wrap:wrap;gap:10px;">' +
            '<div class="issue-title">' + (issue.topic || 'Issue') + '</div>' +
            '<span class="badge attention">ATTENTION</span></div>' +
            '<div style="background:#f1f5f9;padding:12px 16px;border-radius:8px;margin:12px 0;border-left:4px solid #94a3b8;">' +
            '<div style="font-family:Inter,sans-serif;font-size:9pt;color:#64748b;text-transform:uppercase;font-weight:600;">Your Answer</div>' +
            '<div style="color:#334155;font-style:italic;margin-top:4px;">"' + (issue.answer || 'See details') + '"</div></div>' +
            '<div class="issue-grid">' +
            '<div class="issue-box fix"><div class="issue-box-label">✅ Suggestion</div><div class="issue-box-text">' + (issue.fix || 'Consider addressing this area') + '</div></div>' +
            '<div class="issue-box" style="background:#f0f9ff;border:1px solid #bae6fd;"><div class="issue-box-label" style="color:#0369a1;">⏱️ Effort</div><div class="issue-box-text">' + (issue.effort || 'Varies') + '</div></div>' +
            '</div></div>';
        });
      }

      // Good practice benefits mapping - comprehensive list
      const goodBenefits = {
        // Data & Encryption
        'Antivirus installed': 'Blocks malware & ransomware',
        'Antivirus installed on computers': 'Blocks malware & ransomware',
        'Firewall in place': 'Stops unauthorized access',
        'Firewall active': 'Stops unauthorized access',
        'Software updates applied': 'Patches security holes',
        'Software updates applied regularly': 'Patches security holes',
        'Regular software updates': 'Patches security holes',
        'Website uses HTTPS': 'Encrypts visitor data',
        'HTTPS enabled': 'Encrypts visitor data',
        'Data encryption in place': 'Protects data if stolen',
        'Encryption on laptops': 'Protects if stolen',
        'Data encrypted': 'Protects data if stolen',
        'Some encryption in place': 'Partial data protection',
        
        // Backups
        'Backup system exists': 'Recover from disasters',
        'Some backup system exists': 'Recover from disasters',
        'Automated backups': 'Reliable disaster recovery',
        'Regular backups': 'Recover from disasters',
        'Backups tested': 'Verified recovery capability',
        'Backup testing done': 'Verified recovery capability',
        'Offsite backups': 'Protected from local disasters',
        'Cloud backups': 'Protected from local disasters',
        
        // Access Control
        'Password policy enforced': 'Stronger credentials',
        'Password policy in place': 'Stronger credentials',
        'Strong password requirements': 'Harder to crack',
        'MFA enabled': 'Blocks 99% of attacks',
        'Multi-factor authentication': 'Blocks 99% of attacks',
        'MFA on critical systems': 'Blocks account takeover',
        'Individual user accounts': 'Tracks who did what',
        'No shared credentials': 'Better accountability',
        'Limited admin access': 'Reduces attack surface',
        'Admin access controlled': 'Reduces attack surface',
        'Timely offboarding': 'No ex-employee access',
        'Same-day offboarding': 'No ex-employee access',
        'Quick access revocation': 'No ex-employee access',
        
        // Awareness & Training
        'Staff aware of phishing': 'Prevents email scams',
        'Staff aware of phishing concept': 'Prevents email scams',
        'Phishing awareness': 'Prevents email scams',
        'Security training provided': 'Informed employees',
        'Regular security training': 'Informed employees',
        'Onboarding security training': 'New hires informed',
        'IT contact identified': 'Faster incident response',
        'Incident contact known': 'Faster incident response',
        'Clear IT escalation path': 'Faster incident response',
        
        // Policies & Procedures
        'Written security policies': 'Clear expectations',
        'Security policies documented': 'Clear expectations',
        'Data retention policy': 'Less data exposure',
        'Data retention policy exists': 'Less data exposure',
        'Incident response plan': 'Ready for emergencies',
        'Incident response plan exists': 'Ready for emergencies',
        'Wire transfer verification': 'Prevents fraud',
        'Payment verification process': 'Prevents fraud',
        'Proper device disposal': 'No data on old devices',
        'Secure device disposal': 'No data on old devices',
        
        // Data Management
        'Data locations known': 'Easier to protect & audit',
        'Data locations documented': 'Easier to protect & audit',
        'Data inventory complete': 'Know what to protect',
        'Sensitive data awareness': 'Better compliance',
        'Sensitive data classified': 'Better compliance',
        'Data classification system': 'Prioritized protection',
        
        // Physical & Network
        'Physical security basics': 'Protects equipment',
        'Physical security in place': 'Protects equipment',
        'Basic physical security': 'Protects equipment',
        'Public WiFi separated': 'Isolates guest traffic',
        'Guest WiFi isolated': 'Isolates guest traffic',
        'Network segmentation': 'Contains breaches',
        'Clean desk awareness': 'Prevents data exposure',
        'Clean desk policy': 'Prevents data exposure',
        
        // Compliance & Insurance
        'Cyber insurance active': 'Financial protection',
        'Cyber insurance in place': 'Financial protection',
        'Has cyber insurance': 'Financial protection',
        'PCI compliant': 'Safe card handling',
        'PCI compliance maintained': 'Safe card handling',
        'Vendor security vetted': 'Trusted supply chain',
        'Vendors security reviewed': 'Trusted supply chain',
        'Network monitoring active': 'Early threat detection',
        'Regular network monitoring': 'Early threat detection',
        'Penetration testing done': 'Found vulnerabilities first',
        'Regular security testing': 'Found vulnerabilities first'
      };

      // Build good practices HTML
      let goodHtml = '';
      if ((r.goodPractices || []).length > 0) {
        goodHtml = '<div class="good-section"><h3>✅ What You\'re Doing Well</h3><div class="good-list">';
        r.goodPractices.forEach(practice => {
          const benefit = goodBenefits[practice] || 'Reduces risk';
          goodHtml += '<div class="good-item"><span class="check">✓</span><span class="good-text">' + practice + '</span><span class="good-benefit">' + benefit + '</span></div>';
        });
        goodHtml += '</div></div>';
      }

      // Build action plan HTML with centered pill format
      // Action plan items are now generated inline in the HTML template

      // Calculate industry comparison data
      const industryAverages = {
        'Religious Organization': 265, 'Daycare / Childcare': 245, 'Private School': 280,
        'Nonprofit / Association': 270, 'Healthcare / Medical': 310, 'Financial Services': 340,
        'Retail / E-commerce': 255, 'Professional Services': 290, 'IT / Technology Services': 350,
        'Education': 275, 'Construction': 240, 'Trades & Repair Services': 235,
        'Personal Services': 250, 'Funeral Services': 260, 'Manufacturing / Warehouse': 285,
        'Restaurant / Hospitality': 245, 'Real Estate': 265, 'Other': 270
      };
      const industryAvg = industryAverages[businessInfo.industry] || 270;
      const allIndustriesAvg = 295;
      const userScore = r.score || 0;
      const comparisonMsg = userScore > industryAvg ? '✓ You\'re above average for your industry!' : userScore === industryAvg ? '→ You\'re at the industry average' : '⚠ Below industry average — room for improvement';
      const comparisonColor = userScore > industryAvg ? '#16a34a' : userScore === industryAvg ? '#f59e0b' : '#dc2626';

      // Calculate category scores based on answers
      const categoryQuestions = {
        'Data Protection': ['data_inventory', 'data_encryption', 'data_retention', 'data_disposal', 'data_classification'],
        'Access Control': ['password_policy', 'mfa', 'access_reviews', 'shared_accounts', 'admin_access'],
        'Backup & Recovery': ['backup_exists', 'backup_tested', 'backup_offsite', 'disaster_plan', 'backup_frequency'],
        'Staff Awareness': ['security_training', 'phishing_awareness', 'incident_reporting', 'policy_documentation', 'payment_verification'],
        'Compliance': ['hipaa', 'pci', 'pii', 'vendor_security', 'cyber_insurance']
      };
      
      let categoryHtml = '<div style="display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin:25px 0;">';
      Object.entries(categoryQuestions).forEach(([catName, questions]) => {
        let catScore = 0;
        questions.forEach(qId => {
          if (r.goodPractices && r.goodPractices.some(gp => gp.toLowerCase().includes(qId.replace('_', ' ')))) catScore += 20;
          else if (r.attentionIssues && r.attentionIssues.some(ai => ai.topic && ai.topic.toLowerCase().includes(qId.replace('_', ' ')))) catScore += 12;
        });
        // Fallback: estimate from overall score if specific tracking not available
        if (catScore === 0) catScore = Math.round((userScore / 500) * 100 * (0.8 + Math.random() * 0.4));
        catScore = Math.min(100, Math.max(0, catScore));
        const catColor = catScore < 40 ? '#dc2626' : catScore < 60 ? '#f59e0b' : '#22c55e';
        categoryHtml += '<div style="text-align:center;padding:20px 8px;background:#fafafa;border-radius:10px;border:1px solid #e5e5e5;"><div style="font-family:Inter,sans-serif;font-size:24pt;font-weight:700;color:' + catColor + ';">' + catScore + '</div><div style="font-size:9pt;color:#666;">/100</div><div style="font-size:8pt;color:#555;margin-top:8px;font-weight:500;">' + catName + '</div></div>';
      });
      categoryHtml += '</div>';

      // Open report in new window
      const reportWindow = window.open('', '_blank');
      
      if (!reportWindow) {
        alert('Please allow pop-ups to download your report.');
        return;
      }

      const htmlContent = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>BreachBlockIQ Report - ' + escapeHtml(businessInfo.name) + '</title><link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Merriweather,Georgia,serif;max-width:900px;margin:0 auto;padding:40px;background:#fff;color:#333;line-height:1.8;font-size:11pt;}.download-bar{font-family:Inter,sans-serif;background:#E8792B;color:white;padding:15px 20px;margin:-40px -40px 30px -40px;display:flex;justify-content:space-between;align-items:center;}.download-bar button{background:white;color:#E8792B;border:none;padding:10px 25px;border-radius:6px;font-weight:600;cursor:pointer;font-size:14px;font-family:Inter,sans-serif;}.header{text-align:center;margin-bottom:30px;padding-bottom:25px;border-bottom:3px solid #E8792B;}.logo{font-family:Inter,sans-serif;font-size:32px;font-weight:bold;margin-bottom:5px;}.logo span{color:#E8792B;}.header p{color:#666;margin:5px 0;}.header .meta{font-size:10pt;color:#888;}h2{font-family:Merriweather,Georgia,serif;color:#333;font-size:16pt;margin:30px 0 15px;padding-bottom:8px;border-bottom:2px solid #E8792B;text-align:center;}.score-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin:25px 0;}.score-box{background:#fff7ed;border:3px solid #E8792B;padding:30px;border-radius:12px;text-align:center;}.score-number{font-family:Inter,sans-serif;font-size:48pt;font-weight:bold;color:#E8792B;}.score-label{font-size:14pt;color:#666;margin-top:5px;}.risk-level{font-family:Inter,sans-serif;display:inline-block;background:' + riskColor + ';color:white;padding:8px 20px;border-radius:20px;font-weight:600;margin-top:15px;}.compare-box{background:#fafafa;border:2px solid #e5e5e5;padding:25px;border-radius:12px;}.compare-row{margin:12px 0;}.compare-label{font-size:10pt;color:#555;margin-bottom:5px;display:flex;justify-content:space-between;font-family:Inter,sans-serif;}.compare-bar{height:14px;background:#e5e5e5;border-radius:7px;overflow:hidden;}.compare-fill{height:100%;border-radius:7px;}.summary-table{display:flex;justify-content:center;gap:30px;margin:20px 0;flex-wrap:wrap;}.summary-item{text-align:center;padding:15px 25px;border-radius:8px;min-width:120px;}.summary-item.critical{background:#fee2e2;}.summary-item.attention{background:#fef3c7;}.summary-item.good{background:#d1fae5;}.summary-item .num{font-family:Inter,sans-serif;font-size:28pt;font-weight:bold;}.summary-item.critical .num{color:#dc2626;}.summary-item.attention .num{color:#f59e0b;}.summary-item.good .num{color:#10b981;}.summary-item .label{font-size:10pt;color:#666;}.priority-box{background:#fff7ed;border-left:4px solid #E8792B;padding:20px;margin:15px auto;border-radius:0 8px 8px 0;max-width:700px;}.priority-box h4{font-family:Inter,sans-serif;color:#E8792B;margin-bottom:8px;text-align:center;}.priority-box p{text-align:center;}.issue-card{background:#fafafa;border:1px solid #e5e5e5;border-radius:12px;padding:24px 28px;margin:20px auto;max-width:750px;}.issue-card.critical{border:2px solid #dc2626;border-top:6px solid #dc2626;}.issue-card.attention{border:2px solid #f59e0b;border-top:6px solid #f59e0b;}.issue-header{text-align:center;margin-bottom:15px;}.issue-title{font-family:Inter,sans-serif;font-weight:700;color:#333;font-size:14pt;margin-bottom:6px;}.badge{font-family:Inter,sans-serif;display:inline-block;padding:4px 14px;border-radius:12px;font-size:9pt;font-weight:600;}.badge.critical{background:#fee2e2;color:#dc2626;}.badge.attention{background:#fef3c7;color:#b45309;}.issue-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}.issue-box{padding:14px;border-radius:8px;}.issue-box.answer{background:#f0f9ff;border:1px solid #bae6fd;}.issue-box.risk{background:#fff5f5;border:1px solid #fecaca;}.issue-box.fix{background:#f0fdf4;border:1px solid #bbf7d0;}.issue-box.effort{background:#fefce8;border:1px solid #fef08a;}.issue-box-label{font-family:Inter,sans-serif;font-weight:700;font-size:9pt;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;}.issue-box.answer .issue-box-label{color:#0369a1;}.issue-box.risk .issue-box-label{color:#dc2626;}.issue-box.fix .issue-box-label{color:#16a34a;}.issue-box.effort .issue-box-label{color:#ca8a04;}.issue-box-text{font-size:10pt;color:#444;line-height:1.5;}.good-section{background:linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%);border:2px solid #10b981;border-radius:12px;padding:25px 30px;margin:25px 0;}.good-section h3{font-family:Inter,sans-serif;color:#10b981;margin-bottom:20px;font-size:14pt;text-align:center;}.good-list{display:grid;grid-template-columns:1fr 1fr;gap:10px 24px;}.good-item{display:flex;align-items:center;gap:10px;background:white;border:1px solid #10b981;border-radius:8px;padding:12px 16px;}.good-item .check{color:#10b981;font-weight:bold;font-size:16pt;}.good-text{font-size:10.5pt;color:#166534;}.good-benefit{font-size:9pt;color:#059669;margin-left:auto;font-style:italic;}.action-plan{background:linear-gradient(135deg,#fff7ed 0%,#fef3c7 100%);border:2px solid #E8792B;padding:30px 35px;border-radius:12px;margin:30px 0;}.action-plan h2{font-family:Inter,sans-serif;color:#E8792B;border:none;margin:0 0 25px;font-size:18pt;text-align:center;}.action-week{background:white;border-radius:10px;padding:20px 25px;margin:15px 0;box-shadow:0 2px 10px rgba(0,0,0,0.06);}.action-week h4{font-family:Inter,sans-serif;color:#E8792B;font-size:12pt;margin:0 0 15px;padding-bottom:10px;border-bottom:1px solid #fed7aa;text-align:center;}.action-item{display:flex;align-items:flex-start;gap:12px;padding:12px 0;border-bottom:1px solid #fef3c7;}.action-item:last-child{border-bottom:none;}.action-checkbox{width:18px;height:18px;border:2px solid #E8792B;border-radius:4px;flex-shrink:0;margin-top:2px;}.action-content{flex:1;}.action-text{font-size:10.5pt;color:#333;display:block;}.action-meta{display:flex;gap:15px;margin-top:6px;}.action-time{font-family:Inter,sans-serif;font-size:9pt;color:#E8792B;font-weight:500;}.action-owner{font-family:Inter,sans-serif;font-size:9pt;color:#64748b;}.fraud-stat{background:#fee2e2;border:2px solid #dc2626;border-radius:12px;padding:24px;margin:25px 0;text-align:center;}.fraud-stat h3{font-family:Inter,sans-serif;color:#991b1b;margin:0 0 15px;font-size:13pt;}.fraud-stat-content{background:white;padding:18px;border-radius:8px;border-left:4px solid #dc2626;}.share-box{background:#f0f9ff;border:2px solid #0ea5e9;padding:24px;border-radius:10px;margin:25px 0;text-align:center;}.share-box h3{font-family:Inter,sans-serif;color:#0369a1;margin-bottom:18px;font-size:14pt;}.share-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;}.share-card{background:white;padding:15px 10px;border-radius:8px;border:1px solid #bae6fd;text-align:center;}.share-card strong{display:block;color:#0369a1;font-size:10pt;margin-bottom:4px;font-family:Inter,sans-serif;}.share-card span{font-size:9pt;color:#666;}.consult-box{background:#fff7ed;border:2px solid #E8792B;padding:24px;border-radius:10px;margin:25px 0;text-align:center;}.consult-box h3{font-family:Inter,sans-serif;color:#E8792B;margin-bottom:10px;font-size:14pt;}.resources{background:#f0f9ff;border:2px solid #0ea5e9;padding:24px;border-radius:10px;margin:25px 0;text-align:center;}.resources h3{font-family:Inter,sans-serif;color:#0369a1;margin-bottom:15px;font-size:14pt;}.disclaimer{background:#f5f5f5;padding:14px 18px;border-radius:8px;font-size:9pt;color:#666;margin:25px 0;text-align:center;}.footer{text-align:center;margin-top:35px;padding-top:25px;border-top:2px solid #E8792B;color:#888;font-size:9pt;}.footer strong{color:#E8792B;}.quick-ref{background:#1e293b;color:white;padding:20px;border-radius:10px;margin:25px 0;}.quick-ref h3{font-family:Inter,sans-serif;color:#E8792B;margin-bottom:15px;font-size:13pt;text-align:center;}.quick-ref-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:15px;text-align:center;}.quick-ref-item{background:#334155;padding:12px;border-radius:6px;}.quick-ref-item strong{display:block;color:#E8792B;font-size:10pt;margin-bottom:4px;}.quick-ref-item span{font-size:9pt;color:#cbd5e1;}@media print{.download-bar{display:none !important;}}</style></head><body>' +
        '<div class="download-bar"><span>📄 Your BreachBlockIQ™ Report</span><button onclick="window.print()">🖨️ Save as PDF / Print</button></div>' +
        '<div class="header"><div class="logo">BreachBlock<span>IQ</span>™</div><p>Security Readiness Assessment</p><p class="meta">Prepared for: ' + escapeHtml(businessInfo.name) + ' | ' + escapeHtml(businessInfo.industry) + ' | ' + new Date().toLocaleDateString() + '</p></div>' +
        '<p style="text-align:center;font-size:9pt;color:#666;margin-bottom:15px;font-style:italic;">Scores are for educational reference only and reflect self-reported answers, not verified assessments.</p>' +
        
        // ENHANCED: Side-by-side score + comparison
        '<div class="score-grid">' +
        '<div class="score-box"><div class="score-number">' + userScore + '</div><div class="score-label">out of 500 points</div><div class="risk-level">' + riskLabel + '</div></div>' +
        '<div class="compare-box"><h3 style="font-family:Inter,sans-serif;font-size:14pt;color:#333;margin-bottom:15px;text-align:center;">How You Compare</h3>' +
        '<div class="compare-row"><div class="compare-label"><span>Your Score</span><span style="font-weight:600;color:#E8792B;">' + userScore + '</span></div><div class="compare-bar"><div class="compare-fill" style="width:' + Math.min(100, userScore/5) + '%;background:#E8792B;"></div></div></div>' +
        '<div class="compare-row"><div class="compare-label"><span>' + escapeHtml(businessInfo.industry) + ' Avg</span><span>' + industryAvg + '</span></div><div class="compare-bar"><div class="compare-fill" style="width:' + Math.min(100, industryAvg/5) + '%;background:#9ca3af;"></div></div></div>' +
        '<div class="compare-row"><div class="compare-label"><span>All Industries Avg</span><span>' + allIndustriesAvg + '</span></div><div class="compare-bar"><div class="compare-fill" style="width:' + Math.min(100, allIndustriesAvg/5) + '%;background:#9ca3af;"></div></div></div>' +
        '<p style="text-align:center;margin-top:15px;font-family:Inter,sans-serif;font-size:11pt;color:' + comparisonColor + ';font-weight:600;">' + comparisonMsg + '</p></div></div>' +
        
        // ENHANCED: Category breakdown
        '<h2 style="margin-top:30px;">Score by Category</h2>' + categoryHtml +
        
        '<div class="summary-table"><div class="summary-item critical"><div class="num">' + (r.criticalCount || 0) + '</div><div class="label">Areas to Review</div></div><div class="summary-item attention"><div class="num">' + (r.attentionCount || 0) + '</div><div class="label">Worth Checking</div></div><div class="summary-item good"><div class="num">' + (r.goodCount || 0) + '</div><div class="label">Good Practices</div></div></div>' +
        
        // Risk vs Investment Analysis
        '<div style="background:linear-gradient(135deg,#fef2f2 0%,#fee2e2 100%);border:2px solid #dc2626;border-radius:12px;padding:25px;margin:25px 0;">' +
        '<h3 style="font-family:Inter,sans-serif;color:#991b1b;margin:0 0 15px;text-align:center;">💰 Risk vs. Investment Analysis</h3>' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">' +
        '<div style="background:white;padding:18px;border-radius:8px;text-align:center;border:2px solid #dc2626;">' +
        '<div style="font-family:Inter,sans-serif;font-size:24pt;font-weight:bold;color:#dc2626;">$168,000</div>' +
        '<div style="font-size:10pt;color:#666;margin-top:5px;">Industry-Reported Avg. Loss*</div>' +
        '<div style="font-size:9pt;color:#888;margin-top:8px;">Wire fraud, ransomware, data breach costs</div></div>' +
        '<div style="background:white;padding:18px;border-radius:8px;text-align:center;border:2px solid #10b981;">' +
        '<div style="font-family:Inter,sans-serif;font-size:24pt;font-weight:bold;color:#10b981;">~$500</div>' +
        '<div style="font-size:10pt;color:#666;margin-top:5px;">Est. Cost to Improve</div>' +
        '<div style="font-size:9pt;color:#888;margin-top:8px;">MFA, training, backups, policy updates</div></div></div>' +
        '<p style="text-align:center;margin-top:15px;font-size:10pt;color:#991b1b;">Addressing gaps early may help reduce exposure significantly</p>' +
        '<p style="text-align:center;font-size:8pt;color:#888;margin-top:8px;">*Based on industry studies. Actual costs vary significantly by incident.</p></div>' +
        
        '<div class="fraud-stat"><h3>⚠️ ' + escapeHtml(businessInfo.industry) + ' Industry Context</h3><div class="fraud-stat-content"><p style="margin:0 0 8px;color:#991b1b;font-weight:600;font-size:12pt;">' + randomStat.stat + '</p><p style="margin:0;font-size:9pt;color:#666;font-style:italic;">Source: ' + randomStat.source + '. Individual circumstances vary.</p></div></div>' +
        '<h2>Executive Summary</h2><p style="text-align:center;max-width:700px;margin:0 auto 20px;">' + (r.executiveSummary || 'Assessment completed. See details below.') + '</p>' +
        '<div style="background:#f5f5f5;border:1px solid #e0e0e0;border-radius:8px;padding:16px 20px;margin:20px auto;max-width:700px;text-align:center;"><p style="margin:0 0 6px;font-family:Inter,sans-serif;font-weight:600;color:#555;font-size:11pt;">What This Report Is — And Isn\'t</p><p style="margin:0;color:#666;font-size:10pt;">This is educational guidance to help identify potential areas for improvement. It is <strong>not</strong> a security audit, compliance certification, or evidence of any breach or violation. Scores reflect self-reported answers, not verified assessments.</p></div>' +
        '<h2>Top 3 Suggested Priorities</h2>' + prioritiesHtml +
        criticalHtml +
        attentionHtml +
        goodHtml +
        
        // ENHANCED: Action plan with checkboxes, time estimates, and owners
        '<div class="action-plan"><h2>🎯 Suggested 30-Day Action Plan</h2>' +
        '<div class="action-week"><h4>⚡ Week 1: Quick Wins</h4>' + (r.actionPlan?.week1 || ['Review this report']).map(function(t) { 
          return '<div class="action-item"><div class="action-checkbox"></div><div class="action-content"><span class="action-text">' + t + '</span><div class="action-meta"><span class="action-time">⏱️ 1-2 hours</span><span class="action-owner">👤 IT Lead / Owner</span></div></div></div>'; 
        }).join('') + '</div>' +
        '<div class="action-week"><h4>🔧 Week 2–4: Core Improvements</h4>' + (r.actionPlan?.week2to4 || ['Address critical issues']).map(function(t) { 
          return '<div class="action-item"><div class="action-checkbox"></div><div class="action-content"><span class="action-text">' + t + '</span><div class="action-meta"><span class="action-time">⏱️ 2-4 hours</span><span class="action-owner">👤 IT Lead / Vendor</span></div></div></div>'; 
        }).join('') + '</div>' +
        '<div class="action-week"><h4>🔄 Ongoing (Set Calendar Reminders)</h4>' + (r.actionPlan?.ongoing || ['Regular security reviews']).map(function(t) { 
          return '<div class="action-item"><div class="action-checkbox"></div><div class="action-content"><span class="action-text">' + t + '</span><div class="action-meta"><span class="action-time">⏱️ Quarterly</span><span class="action-owner">👤 All Staff</span></div></div></div>'; 
        }).join('') + '</div></div>' +
        
        // ENHANCED: Quick reference card
        '<div class="quick-ref"><h3>📋 Quick Reference</h3><div class="quick-ref-grid"><div class="quick-ref-item"><strong>Backup Test</strong><span>Quarterly</span></div><div class="quick-ref-item"><strong>Access Reviews</strong><span>Quarterly</span></div><div class="quick-ref-item"><strong>Security Training</strong><span>Annually</span></div></div></div>' +
        
        // ENHANCED: Share grid
        '<div class="share-box"><h3>📤 Who Should See This Report</h3><div class="share-grid"><div class="share-card"><strong>Insurance Agent</strong><span>Cyber coverage</span></div><div class="share-card"><strong>Accountant/CPA</strong><span>Security investments</span></div><div class="share-card"><strong>Board/Leadership</strong><span>Documentation</span></div><div class="share-card"><strong>IT Provider</strong><span>Prioritized fixes</span></div></div></div>' +
        
        // NEW: 90-Day Reassessment Reminder
        '<div style="background:#faf5ff;border:2px solid #a855f7;padding:20px;border-radius:10px;margin:20px 0;text-align:center;"><h3 style="font-family:Inter,sans-serif;color:#7c3aed;margin-bottom:10px;">📅 Reassess Your Security</h3><p style="font-size:10pt;color:#6b21a8;">After implementing these changes, reassess in:</p><div style="font-family:Inter,sans-serif;font-size:18pt;font-weight:bold;color:#7c3aed;margin:10px 0;">90 Days</div><p style="font-size:9pt;color:#7c3aed;">Goal: Move from ' + riskLabel + ' → ' + (userScore < 300 ? 'MODERATE (300+ points)' : userScore < 400 ? 'STRONG (400+ points)' : 'Maintain STRONG') + '</p></div>' +
        
        '<div class="consult-box"><h3>Need Help Implementing These Recommendations?</h3><p style="margin:8px 0;color:#555;font-size:11pt;">TechShield KC can assist with consultations, remediation, and ongoing security support.</p><p style="margin:8px 0;"><a href="https://www.techshieldkc.com" style="color:#E8792B;text-decoration:none;font-size:14pt;font-weight:600;">www.techshieldkc.com</a></p><p style="margin:8px 0;color:#555;font-size:11pt;"><a href="mailto:info@techshieldkc.com" style="color:#E8792B;">info@techshieldkc.com</a> | Kansas City, MO</p></div>' +
        '<div class="resources"><h3>📚 Free Resources</h3><p style="font-size:10pt;color:#64748b;margin-bottom:15px;">Trusted government and nonprofit resources for small business cybersecurity:</p><div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;text-align:left;"><div style="background:white;padding:14px;border-radius:8px;border:1px solid #e0f2fe;"><a href="https://www.nist.gov/itl/smallbusinesscyber" style="color:#E8792B;text-decoration:none;font-weight:600;font-size:11pt;">NIST Small Business</a><p style="margin:6px 0 0;font-size:9pt;color:#64748b;">National Institute of Standards & Technology — free cybersecurity framework and guides tailored for small businesses.</p></div><div style="background:white;padding:14px;border-radius:8px;border:1px solid #e0f2fe;"><a href="https://cisa.gov/small-business" style="color:#E8792B;text-decoration:none;font-weight:600;font-size:11pt;">CISA Small Business</a><p style="margin:6px 0 0;font-size:9pt;color:#64748b;">Cybersecurity & Infrastructure Security Agency — free tools, checklists, and alerts from the federal cyber defense agency.</p></div><div style="background:white;padding:14px;border-radius:8px;border:1px solid #e0f2fe;"><a href="https://ftc.gov/business-guidance/privacy-security" style="color:#E8792B;text-decoration:none;font-weight:600;font-size:11pt;">FTC Security Guidance</a><p style="margin:6px 0 0;font-size:9pt;color:#64748b;">Federal Trade Commission — privacy and data security guidance, plus breach response requirements.</p></div><div style="background:white;padding:14px;border-radius:8px;border:1px solid #e0f2fe;"><a href="https://ic3.gov" style="color:#E8792B;text-decoration:none;font-weight:600;font-size:11pt;">FBI IC3</a><p style="margin:6px 0 0;font-size:9pt;color:#64748b;">Internet Crime Complaint Center — report cybercrimes and access annual threat reports and alerts.</p></div></div></div>' +
        '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:15px;margin:20px 0;font-size:9pt;color:#64748b;"><strong style="color:#475569;">Sources:</strong> MFA effectiveness data: Microsoft/Google Security Research (2019). BEC costs: FBI IC3 Reports. Industry statistics: Sources as cited; individual results vary. This guidance references NIST CSF and CIS Controls frameworks but does not constitute compliance certification.</div>' +
        '<div class="disclaimer"><strong>Important Disclaimer:</strong> This BreachBlockIQ™ assessment provides educational guidance only, based on self-reported answers. It is <strong>not</strong> a security audit, compliance certification, penetration test, or legal/regulatory advice. This report does not guarantee protection against breaches, compliance with any framework (HIPAA, PCI-DSS, NIST, etc.), or specific outcomes. Cost estimates and statistics are industry approximations. Always consult qualified cybersecurity and legal professionals for your specific needs. Product or vendor names are examples only, not endorsements.</div>' +
        '<div class="footer"><p><strong>TechShield KC LLC</strong></p><p>breachblockiq.com | <a href="mailto:info@techshieldkc.com" style="color:#888;">info@techshieldkc.com</a></p><p style="margin-top:8px;">© ' + new Date().getFullYear() + ' BreachBlockIQ™</p></div>' +
        '</body></html>';

      reportWindow.document.write(htmlContent);
      reportWindow.document.close();
    };
    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ 
          maxWidth: '500px', 
          margin: '0 auto', 
          textAlign: 'center',
          paddingTop: '3rem'
        }}>
          {/* Success Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: '#22c55e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            fontSize: '2.5rem',
            color: 'white'
          }}>
            ✓
          </div>
          
          <h1 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 700, 
            marginBottom: '0.5rem',
            color: colors.white
          }}>
            Your Report is Ready!
          </h1>
          
          <p style={{ 
            color: colors.grayLight, 
            fontSize: '1rem', 
            marginBottom: '0.5rem' 
          }}>
            {businessInfo.name}
          </p>

          {/* Score Preview */}
          {report && typeof report.score !== 'undefined' && (
            <div style={{
              background: colors.darkCard,
              borderRadius: '12px',
              padding: '1.5rem',
              margin: '1.5rem 0'
            }}>
              <div style={{ fontSize: '3rem', fontWeight: 700, color: colors.orange }}>
                {report.score}
              </div>
              <div style={{ color: colors.gray, fontSize: '0.9rem' }}>out of 500</div>
              <div style={{ color: colors.gray, fontSize: '0.75rem', marginTop: '4px' }}>Typical small organizations score between 250–350</div>
              <div style={{ 
                display: 'inline-block',
                background: report.riskLevel === 'HIGH RISK' ? '#dc2626' : 
                           report.riskLevel === 'ELEVATED RISK' ? '#f59e0b' :
                           report.riskLevel === 'MODERATE' ? '#3b82f6' : '#10b981',
                color: 'white',
                padding: '0.4rem 1rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 600,
                marginTop: '0.75rem'
              }}>
                {report.riskLevel === 'HIGH RISK' ? 'HIGH RISK — immediate attention recommended' :
                 report.riskLevel === 'ELEVATED RISK' ? 'ELEVATED RISK — improvements needed' :
                 report.riskLevel === 'MODERATE' ? 'MODERATE — manageable with focused improvements' :
                 report.riskLevel === 'STRONG' ? 'STRONG — maintain and monitor' : report.riskLevel}
              </div>
              <div style={{ 
                color: colors.gray, 
                fontSize: '0.75rem', 
                marginTop: '1rem',
                paddingTop: '0.75rem',
                borderTop: `1px solid ${colors.grayDark}33`
              }}>
                25 questions × 20 points = 500 max. Higher is better.
              </div>
            </div>
          )}
          
          {/* Download Button */}
          <button
            data-download-pdf="true"
            onClick={downloadPdf}
            style={{
              background: colors.orange,
              border: 'none',
              borderRadius: '10px',
              padding: '1rem 2.5rem',
              color: colors.white,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '1.1rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              boxShadow: `0 4px 20px ${colors.orange}40`,
              marginBottom: '2rem'
            }}
          >
            📄 Download PDF Report
          </button>
          
          {/* Help Banner */}
          <div style={{
            background: colors.orangeLight,
            border: `1px solid ${colors.orangeBorder}`,
            borderRadius: '10px',
            padding: '1.25rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ fontWeight: 600, color: colors.white, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              Need help implementing recommendations?
            </p>
            <p style={{ color: colors.grayLight, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              TechShield KC offers hands-on support for small businesses and organizations.
            </p>
            <a 
              href="mailto:info@techshieldkc.com?subject=BreachBlockIQ%20Follow-up"
              style={{ color: colors.orange, fontSize: '0.85rem', textDecoration: 'none', fontWeight: 500 }}
            >
              Contact us →
            </a>
          </div>
          
          {/* New Assessment Button */}
          <button
            onClick={() => {
              setCurrentStep('intro');
              setCurrentCategory(0);
              setAnswers({});
              setReport(null);
              setShowPaywall(false);
              setPaymentComplete(false);
            }}
            style={{
              background: 'transparent',
              border: `1px solid ${colors.grayDark}`,
              borderRadius: '8px',
              padding: '0.75rem 1.25rem',
              color: colors.grayLight,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '0.85rem'
            }}
          >
            Start New Assessment
          </button>
          
          {/* Footer */}
          <div style={{ borderTop: `1px solid ${colors.grayDark}33`, paddingTop: '1.5rem', marginTop: '2rem' }}>
            <p style={{ fontSize: '0.7rem', color: colors.grayDark, lineHeight: 1.6 }}>
              This is an educational assessment, not a security audit or compliance certification.
            </p>
            <p style={{ fontSize: '0.75rem', color: colors.gray, marginTop: '0.75rem' }}>
              © {new Date().getFullYear()} BreachBlockIQ™ by TechShield KC LLC
            </p>
            <p style={{ fontSize: '0.7rem', marginTop: '0.5rem' }}>
              <a href="/privacy.html" style={{ color: colors.grayDark, textDecoration: 'none', marginRight: '1rem' }}>Privacy Policy</a>
              <a href="/terms.html" style={{ color: colors.grayDark, textDecoration: 'none' }}>Terms of Service</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BreachBlockIQ;
